/**
 * Global Precharting paste parser.
 *
 * Takes free text copied from an EHR (eClinicalWorks-style summaries) or from our own
 * template and converts it into the exact data shapes used by the Precharting section forms.
 *
 * Design rules (accuracy first):
 *  - 100% deterministic and offline. No guessing: if a line cannot be classified with
 *    confidence it is returned in `unrecognized` instead of being silently dropped or forced
 *    into a field.
 *  - Numbers are never stored with an unrecognised unit, and unit conversions are explicit.
 *  - Only sections that actually appear in the pasted text are returned, so applying a paste
 *    never touches sections the user did not paste.
 *
 * Output shape of parsePrechartingText():
 * {
 *   sections: {            // only keys whose heading was found in the text
 *     problemList:       { items: [{ id, code, description, sourceLine, ... }], warnings: [] },
 *     medicationList:    { items: [medicationEntry], warnings: [] },
 *     vitalsLabs:        { data: <vitalsLabs form shape>, mapped: [...], warnings: [] },
 *     surgicalHistory:   { items: [{ id, date, surgery }], warnings: [] },
 *     hospitalization:   { items: [{ id, date, reason }], warnings: [] },
 *     diagnosticImaging: { items: [{ id, date, locationContrast, indication, result }], warnings: [] },
 *     procedures:        { items: [{ id, date, name, indication, result }], warnings: [] },
 *     specialistsVisits: { items: [{ id, date, specialty, specialistName, details }], warnings: [] },
 *   },
 *   unrecognized: [{ section, lineNumber, text, reason }],
 *   skipped:      [{ section, lineNumber, text, reason }],   // recognised but not tracked by the chart
 *   ignoredSections: [{ heading, lineCount }],
 *   headingsFound: number,
 * }
 */

/* -------------------------------------------------------------------------- */
/* Generic helpers                                                            */
/* -------------------------------------------------------------------------- */

let idSeq = 0;
const nextId = () => Date.now() + idSeq++;

const collapse = (s) => String(s).replace(/\s+/g, ' ').trim();

const stripEmptyBrackets = (s) => s.replace(/\(\s*\)|\[\s*\]/g, ' ');

const trimDelims = (s) => {
  let out = collapse(stripEmptyBrackets(s));
  out = out.replace(/^[-\s:;,|.]+/, '').replace(/[-\s:;,|]+$/, '');
  // strip a trailing period unless it ends an abbreviation (Dr., St., single letter)
  if (/\.$/.test(out) && !/(?:\b[A-Za-z]|\b(?:dr|mr|mrs|ms|sr|jr|st|vs|inc|etc))\.$/i.test(out)) {
    out = out.slice(0, -1);
  }
  return out.trim();
};

const cleanLine = (raw) => {
  let s = String(raw)
    .replace(/[\u00A0\u2007\u202F]/g, ' ')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\t/g, '  ');
  s = s.trim();
  // bullets / numbering ("- ", "* ", "• ", "1. ", "2) ")
  s = s.replace(/^(?:[-*\u2022\u25CF\u25AA\u25E6]|\d{1,3}[.)])\s+/, '');
  return s.trim();
};

const hasLetters = (s) => /[A-Za-z]/.test(s);

/* -------------------------------------------------------------------------- */
/* Dates                                                                      */
/* -------------------------------------------------------------------------- */

const MONTH_NUM = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};
const MONTH_RE =
  '(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';

const expandYear = (yy) => {
  if (yy >= 100) return yy;
  const currentYear = new Date().getFullYear();
  const full = 2000 + yy;
  return full > currentYear + 1 ? 1900 + yy : full;
};

const makeDate = (y, m, d, approx = false) => {
  if (!y || y < 1900 || y > 2100) return null;
  if (m !== null && m !== undefined && (m < 1 || m > 12)) return null;
  if (d !== null && d !== undefined && (d < 1 || d > 31)) return null;
  return { y, m: m || null, d: d || null, approx };
};

/**
 * Finds the earliest date in `text`. Returns { y, m, d, approx, index, length } or null.
 *  opts.twoPart  - also treat "MM/YY" as month/year (flagged `approx`)
 *  opts.yearOnly - also accept a bare 4-digit year
 */
export function findDate(text, opts = {}) {
  const found = [];
  const add = (match, build) => {
    const dt = build(match);
    if (dt) found.push({ ...dt, index: match.index, length: match[0].length });
  };
  let m;

  const iso = /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g;
  while ((m = iso.exec(text))) add(m, (x) => makeDate(+x[1], +x[2], +x[3]));

  const us = /\b(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})\b/g;
  while ((m = us.exec(text))) add(m, (x) => makeDate(expandYear(+x[3]), +x[1], +x[2]));

  const monthYear = /\b(\d{1,2})\/(\d{4})\b/g;
  while ((m = monthYear.exec(text))) add(m, (x) => makeDate(+x[2], +x[1], null));

  if (opts.twoPart) {
    const twoPart = /\b(\d{1,2})\/(\d{2})\b(?!\/)/g;
    while ((m = twoPart.exec(text))) {
      add(m, (x) => makeDate(expandYear(+x[2]), +x[1], null, true));
    }
  }

  const monDayYear = new RegExp(`\\b${MONTH_RE}\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})\\b`, 'gi');
  while ((m = monDayYear.exec(text))) {
    add(m, (x) => makeDate(+x[3], MONTH_NUM[x[1].toLowerCase().slice(0, 3)], +x[2]));
  }

  const dayMonYear = new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+${MONTH_RE}\\.?,?\\s+(\\d{4})\\b`, 'gi');
  while ((m = dayMonYear.exec(text))) {
    add(m, (x) => makeDate(+x[3], MONTH_NUM[x[2].toLowerCase().slice(0, 3)], +x[1]));
  }

  const monYear = new RegExp(`\\b${MONTH_RE}\\.?,?\\s+(\\d{4})\\b`, 'gi');
  while ((m = monYear.exec(text))) {
    add(m, (x) => makeDate(+x[2], MONTH_NUM[x[1].toLowerCase().slice(0, 3)], null));
  }

  if (opts.yearOnly) {
    const yearOnly = /\b((?:19|20)\d{2})\b/g;
    while ((m = yearOnly.exec(text))) add(m, (x) => makeDate(+x[1], null, null));
  }

  if (!found.length) return null;
  found.sort((a, b) => a.index - b.index || b.length - a.length);
  return found[0];
}

const removeSpan = (text, span) => text.slice(0, span.index) + ' ' + text.slice(span.index + span.length);

const pad2 = (n) => String(n).padStart(2, '0');

/** MM/DD/YY (full date) | MM/YYYY (month + year) | YYYY (year only) */
export const formatDateFull = (dt) => {
  if (!dt) return '';
  if (dt.d) return `${pad2(dt.m)}/${pad2(dt.d)}/${String(dt.y).slice(-2)}`;
  if (dt.m) return `${pad2(dt.m)}/${dt.y}`;
  return String(dt.y);
};

/** MM/YY (month + year) | YYYY (year only) */
export const formatDateMoYr = (dt) => {
  if (!dt) return '';
  if (dt.m) return `${pad2(dt.m)}/${String(dt.y).slice(-2)}`;
  return String(dt.y);
};

const dateSortKey = (dt) => (dt ? dt.y * 10000 + (dt.m || 0) * 100 + (dt.d || 0) : -1);

/* -------------------------------------------------------------------------- */
/* Section headings                                                           */
/* -------------------------------------------------------------------------- */

const normalizeHeading = (s) =>
  String(s)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const TARGET_HEADINGS = {
  problemList: [
    'problem list', 'problems', 'active problems', 'active problem list', 'diagnoses',
    'diagnosis list', 'icd 10 codes', 'icd10 codes', 'icd 10 cm codes', 'icd10 cm codes',
    'problem list icd 10 cm codes', 'past medical history', 'medical history', 'pmh',
    'chronic conditions', 'chronic problems',
  ],
  medicationList: [
    'medications', 'medication list', 'current medications', 'active medications',
    'medication', 'meds', 'current meds', 'current outpatient medications',
    'outpatient medications', 'prescriptions', 'current prescriptions',
  ],
  vitalsLabs: [
    'vitals', 'vital signs', 'vitals and labs', 'vitals labs', 'labs and vitals', 'labs',
    'lab results', 'laboratory', 'laboratory results', 'lab data', 'recent labs',
    'recent vitals', 'lab', 'labs and results', 'labs di reports', 'labs reports', 'labs di',
  ],
  surgicalHistory: [
    'surgical history', 'past surgical history', 'psh', 'surgeries', 'surgery history',
    'surgery', 'surgical hx',
  ],
  hospitalization: [
    'hospitalizations', 'hospitalization', 'hospitalization history', 'hospital history',
    'hospital admissions', 'admissions', 'hospitalizations history',
    // eClinicalWorks
    'hospitalization major diagnostic procedure', 'hospitalization major diagnostic procedures',
    'hospitalizations major diagnostic procedures', 'major diagnostic procedure',
  ],
  diagnosticImaging: [
    'diagnostic imaging', 'imaging', 'radiology', 'imaging results', 'diagnostic tests',
    'diagnostic studies', 'diagnostic imaging and tests', 'imaging studies',
  ],
  procedures: ['procedures', 'procedure', 'procedure history', 'procedures performed'],
  specialistsVisits: [
    'specialist visits', 'specialists visits', 'specialists', 'specialist', 'specialty visits',
    'consults', 'consultations', 'specialist consults', 'referrals', 'specialist visit',
  ],
};

const TARGET_LOOKUP = {};
Object.entries(TARGET_HEADINGS).forEach(([key, names]) => {
  names.forEach((n) => {
    TARGET_LOOKUP[normalizeHeading(n)] = key;
  });
});

// Headings of sections that must NOT leak into a previous section (their lines are ignored).
const IGNORED_HEADINGS = new Set(
  [
    'allergies', 'allergy', 'drug allergies', 'allergies and intolerances', 'family history',
    'social history', 'immunizations', 'immunization history', 'immunization', 'health maintenance',
    'review of systems', 'ros', 'hpi', 'history of present illness', 'chief complaint', 'cc',
    'subjective', 'objective', 'assessment', 'plan', 'assessment and plan', 'a p',
    'physical exam', 'exam', 'patient information', 'demographics', 'patient demographics',
    'insurance', 'notes', 'clinical notes', 'care team', 'preventive care', 'screenings',
    'orders', 'resolved problems', 'resolved', 'inactive problems', 'inactive',
    'historical problems', 'social hx', 'family hx', 'smoking status', 'tobacco use',
    'visit information', 'encounter', 'patient', 'followup', 'follow up',
    // eClinicalWorks
    'allergies intolerance lack of therapeutic effect', 'allergies intolerance',
    'allergies intolerances', 'reason for appointment', 'reason for visit',
    'depression screening', 'phq 9', 'phq9', 'alcohol screening', 'audit c',
  ].map(normalizeHeading),
);

// Lines that carry no data: page markers, scroll prompts and rule lines of an EHR export.
const NOISE_LINE_RE =
  /^(?:page\s+\d+(?:\s+of\s+\d+)?\.?|\(?\s*please\s+scroll\s+down\s*\)?\.?|[=_*~-]{3,}.*)$/i;

// Visit header lines at the top of an export (before the first section heading).
const META_LINE_RE =
  /^(?:precharting information|patient demographics|date of service|date of birth|dob|reason for (?:appointment|visit))\b/i;

const MED_STATUS_HEADINGS = {
  taking: 'taking', 'currently taking': 'taking', active: 'taking', current: 'taking',
  'not taking': 'not-taking', 'not currently taking': 'not-taking', 'on hold': 'not-taking',
  held: 'not-taking', suspended: 'not-taking',
  discontinued: 'discontinued', stopped: 'discontinued', inactive: 'discontinued',
  completed: 'discontinued', expired: 'discontinued', historical: 'discontinued',
  'previously taking': 'discontinued',
};

const PANEL_RE =
  /\b(?:panel|cbc|cmp|bmp|lipids?|thyroid|kidney|renal|liver|hepatic|electrolytes?|glucose|diabetes|parathyroid|ra|rheumatoid|metabolic|hematology|chemistry|urinalysis|urine|vitals?|labs?|inflammatory|cardiac|results?)\b/;

function detectHeading(text, current) {
  const colonIdx = text.indexOf(':');
  const head = (colonIdx > -1 ? text.slice(0, colonIdx) : text).replace(
    /\s*\(\s*\d+\s*(?:items?|total)?\s*\)\s*$/i,
    '',
  );
  const tail = colonIdx > -1 ? text.slice(colonIdx + 1).trim() : '';
  const norm = normalizeHeading(head);
  if (!norm) return null;
  const wordCount = norm.split(' ').length;
  if (wordCount > 7) return null;

  if (current && current.key === 'medicationList') {
    // "Not-Taking" normalises to "not taking" (non-alphanumerics become spaces)
    const status = MED_STATUS_HEADINGS[norm];
    if (status) return { type: 'status', status, remainder: tail };
  }

  const targetKey = TARGET_LOOKUP[norm];
  if (targetKey) return { type: 'target', key: targetKey, remainder: tail };

  if (IGNORED_HEADINGS.has(norm)) return { type: 'ignored', heading: collapse(head) };

  if (tail === '' && text.trim().endsWith(':') && !/\d/.test(norm)) {
    if (current && current.key === 'vitalsLabs' && PANEL_RE.test(norm)) {
      return { type: 'subheading' };
    }
    return { type: 'ignored', heading: collapse(head) };
  }
  return null;
}

const COLUMN_HEADER_WORDS = new Set([
  'date', 'procedure', 'procedures', 'name', 'surgery', 'reason', 'specialty', 'specialist',
  'details', 'detail', 'indication', 'result', 'results', 'location', 'contrast', 'test', 'tests',
  'sl', 'no', 'visit', 'of', 'hospitalization', 'imaging', 'facility', 'provider', 'last',
  'mo', 'yr', 'mm', 'dd', 'yy', 'component', 'value', 'units', 'unit', 'reference', 'range',
  'flag', 'status', 'code', 'description', 'icd', 'medication', 'dosage', 'frequency', 'route',
  'timing', 'other', 'misc', 'and', 'the', 'type', 'sig', 'drug', 'collected', 'ordered',
]);

const isColumnHeaderLine = (text) => {
  if (/\d/.test(text)) return false;
  const tokens = normalizeHeading(text).split(' ').filter(Boolean);
  return tokens.length >= 2 && tokens.every((t) => COLUMN_HEADER_WORDS.has(t));
};

/* -------------------------------------------------------------------------- */
/* Problem list                                                               */
/* -------------------------------------------------------------------------- */

const RESOLVED_TAG_RE = /(?:[([]|\s-\s)\s*(?:resolved|inactive|historical)\s*[)\]]?\s*$|^\s*(?:resolved|inactive)\s*[:-]/i;

/**
 * Returns accepted ICD-10-CM code spans in a line. A code-like token (e.g. "E11.9", "I10") is only
 * accepted when it is clearly delimited (line start/end, brackets, dash/colon/comma separators or an
 * explicit ICD tag). That prevents "Vitamin B12", "T12 vertebra" and similar text being read as codes.
 */
export function extractIcdCodes(line) {
  const results = [];
  const re = /\b([A-TV-Za-tv-z]\d{2}(?:\.\d{1,4})?)\b/g;
  let m;
  while ((m = re.exec(line))) {
    const start = m.index;
    const end = start + m[0].length;
    if (start > 0 && line[start - 1] === '.') continue;
    const before = line.slice(0, start);
    const after = line.slice(end);
    if (/(?:vitamin|vit\.?|hepatitis|hep|factor)\s*$/i.test(before)) continue;

    const beforeTrim = before.trim();
    const atStart = beforeTrim === '' || /^(?:icd[\s-]?10(?:[\s-]?cm)?\s*(?:code)?\s*[:#-]?)$/i.test(beforeTrim);
    const atEnd = after.trim() === '' || /^[\s.,;)\]]*$/.test(after);
    const wrapped = /[([]\s*$/.test(before) && /^\s*[)\]]/.test(after);
    const delimBefore = /(?:[-:|,;([]|icd[\s-]?10(?:[\s-]?cm)?\s*[:#-]?)\s*$/i.test(before);
    const delimAfter = /^\s*[-:|,;)\]]/.test(after);

    // Secondary codes on an eCW diagnosis line: "I10 -- ... /I12.9 - ..." or "M79.10 - ... / ??M62.830 ...??/ M51.369".
    // Only accepted after a first code and only with a decimal part, so "B12" or "T12" are never read as codes.
    const slashDelimBefore = results.length > 0 && m[1].includes('.') && /[/?&+]\s*$/.test(before);
    const softAfter = after === '' || /^[\s.,;:)\]?!\u2026-]/.test(after);

    if (atStart || atEnd || wrapped || (delimBefore && delimAfter) || (slashDelimBefore && softAfter)) {
      results.push({ code: m[1].toUpperCase(), index: start, length: m[0].length });
    }
  }
  const seen = new Set();
  return results.filter((r) => (seen.has(r.code) ? false : (seen.add(r.code), true)));
}

function parseProblems(entries, ctx) {
  const items = [];
  const seen = new Set();
  entries.forEach((entry) => {
    const text = entry.text;
    if (isColumnHeaderLine(text)) return;
    if (RESOLVED_TAG_RE.test(text)) {
      ctx.unrecognized(entry, 'Marked resolved/inactive, so it was not added to the active problem list');
      return;
    }
    const codes = extractIcdCodes(text);
    if (!codes.length) {
      ctx.unrecognized(entry, 'No ICD-10-CM code found on this line (a code is required for the problem list)');
      return;
    }

    let desc = text;
    [...codes].sort((a, b) => b.index - a.index).forEach((c) => {
      desc = desc.slice(0, c.index) + ' ' + desc.slice(c.index + c.length);
    });
    // a code repeated later on the same line ("E66.01 -- Severe obesity ... - E66.01") is not extra text
    codes.forEach((c) => {
      desc = desc.replace(new RegExp(`\\b${c.code.replace('.', '\\.')}\\b`, 'gi'), ' ');
    });
    desc = desc.replace(/\bICD[\s-]?10(?:[\s-]?CM)?\s*(?:codes?)?\s*[:#-]?/gi, ' ');
    desc = trimDelims(desc.replace(/[()[\]]/g, ' ').replace(/\s*[,;|]\s*(?=[,;|]|$)/g, ' '));
    if (codes.length > 1) {
      ctx.warn(entry, `${codes.length} codes on one line; descriptions will be filled from the ICD-10 database`);
      desc = '';
    } else if (/\b[A-TV-Z]\d{2}(?:\.\d{1,4})?\b/.test(desc)) {
      ctx.warn(entry, 'Line contains additional code-like text that was kept in the description; please review');
    }

    codes.forEach((c) => {
      if (seen.has(c.code)) return;
      seen.add(c.code);
      items.push({
        id: `${nextId()}`,
        code: c.code,
        description: desc,
        sourceLine: entry.text,
        lineNumber: entry.lineNumber,
      });
    });
  });
  return items;
}

/* -------------------------------------------------------------------------- */
/* Medications                                                                */
/* -------------------------------------------------------------------------- */

const SIG_VERBS =
  'take|takes|inject|apply|use|instill|inhale|place|insert|chew|dissolve|swallow|give|administer|rub|sprinkle|drink|infuse';
const SIG_VERB_RE = new RegExp(`\\b(?:${SIG_VERBS})\\b`, 'i');

const FORM_WORDS =
  'tablets?|tabs?|capsules?|caps?|injection|solution|suspension|cream|ointment|gel|lotion|drops?|spray|patch(?:es)?|powder|syrup|elixir|film|pen|inhaler|nebulizer|liquid|suppository|lozenge|kit|disk|diskus|ellipta|concentrate|emulsion|foam|shampoo|paste|granules|packet|pack';
const FORM_MODS =
  '(?:\\d+\\s*(?:hr|hrs|hour|hours)|extended[- ]release|delayed[- ]release|sustained[- ]release|enteric[- ]coated|film[- ]coated|chewable|orally\\s+disintegrating|oral|ophthalmic|topical|nasal|transdermal|er|xr|sr|dr|cr|odt)';
// "powder for injection", "powder for reconstitution", "tablet for oral suspension" stay one form
const FORM_FOR =
  '(?:\\s+for\\s+(?:injection|reconstitution|oral\\s+suspension|oral\\s+solution|suspension|solution|inhalation|nebulization))?';
const FORM_RE = new RegExp(
  `(?:\\b${FORM_MODS}[\\s,]+)*\\b(?:${FORM_WORDS})\\b${FORM_FOR}(?:\\s*,?\\s*${FORM_MODS}\\b)*`,
  'i',
);

const NUM = '\\d+(?:\\.\\d+)?';
const S_UNIT = '(?:mg|mcg|ug|\\u00B5g|\\u03BCg|gm|g|kg|ml|l|units?|iu|meq|mmol|%)';
const PER = '(?:\\s*\\/\\s*(?:' + NUM + '\\s*)?(?:ml|l|g|mg|mcg|hr|h|actuation|inh|puff|spray|dose|day)(?![A-Za-z]))?';
const STRENGTH_SINGLE = `${NUM}\\s*${S_UNIT}(?![A-Za-z])${PER}`;
const STRENGTH_COMBO = `${NUM}(?:\\s*-\\s*${NUM})+\\s*${S_UNIT}(?![A-Za-z])${PER}`;
const STRENGTH_SRC = `(?:${STRENGTH_COMBO}|${STRENGTH_SINGLE}(?:\\s*[-/]\\s*${STRENGTH_SINGLE})*)`;

const QTY_RE = /\b\d+(?:\/\d+)?(?:\.\d+)?\s*(?:tab(?:let)?s?|caps?(?:ules?)?|puffs?|drops?|gtt|sprays?|patch(?:es)?|applications?)(?:\(s\))?(?![/\w])/i;

const ROUTES = [
  { re: /\b(?:by\s+mouth|orally|oral|po|per\s+os)\b/i, value: 'orally' },
  { re: /\b(?:subcutaneously|subcutaneous|subcut|subq|sub-q|sq|sc)\b/i, value: 'subcutaneously' },
  { re: /\b(?:intramuscularly|intramuscular)\b|\bIM\b/, value: 'intramuscularly' },
  { re: /\b(?:intravenously|intravenous|iv)\b/i, value: 'intravenously' },
  { re: /\b(?:topically|topical|applied\s+topically)\b/i, value: 'topically' },
  { re: /\b(?:by\s+inhalation|inhalation|inhaled|inhale|inh)\b/i, value: 'inhaled' },
  { re: /\b(?:intranasally|intranasal|nasally|nasal|each\s+nostril)\b/i, value: 'intranasally' },
  { re: /\b(?:sublingually|sublingual|under\s+the\s+tongue)\b/i, value: 'sublingually' },
  { re: /\b(?:ophthalmic|(?:in|into)\s+(?:each|both|the|either)\s+(?:affected\s+|left\s+|right\s+)*eyes?|each\s+eye|both\s+eyes)\b/i, value: null },
  { re: /\b(?:otic|(?:in|into)\s+(?:each|both|the|either)\s+(?:affected\s+|left\s+|right\s+)*ears?)\b/i, value: null },
  { re: /\b(?:rectally|rectal|per\s+rectum)\b/i, value: 'rectally' },
  { re: /\b(?:vaginally|vaginal)\b/i, value: 'vaginally' },
  { re: /\b(?:transdermally|transdermal)\b/i, value: 'transdermally' },
];
// Route words that safely mark where the "sig" starts (excluding the ambiguous bare "oral" used in forms)
const SIG_ROUTE_RE =
  /\b(?:by\s+mouth|orally|po|subcutaneously|subcutaneous|subq|sq|intramuscularly|intravenously|topically|inhaled|by\s+inhalation|intranasally|sublingually|rectally)\b/i;

const NUMBER_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, once: 1, twice: 2, thrice: 3 };

const timesPhrase = (n, unit) => {
  const u = /^week/i.test(unit) ? 'week' : /^month/i.test(unit) ? 'month' : 'day';
  if (n === 1) return `once a ${u}`;
  return `${n} times a ${u}`;
};

/** Returns { value, index, length } or null. Order matters (specific patterns first). */
export function parseFrequency(text) {
  const tests = [
    [/\bq\.?\s*(\d+)\s*(?:h|hr|hrs|hours?)\b/i, (m) => `every ${m[1]} hours`],
    [/\bevery\s+other\s+day\b|\bqod\b|\bevery\s+2\s+days\b/i, () => 'every other day'],
    [
      /\bevery\s+(\d+)\s*(hours?|hrs?|days?|weeks?|months?)\b/i,
      (m) => {
        const n = +m[1];
        const unit = m[2].toLowerCase();
        if (/^h/.test(unit)) return `every ${n} hours`;
        if (/^d/.test(unit)) return n === 1 ? 'once a day' : `every ${n} days`;
        if (/^w/.test(unit)) return n === 1 ? 'once a week' : `every ${n} weeks`;
        return n === 1 ? 'once a month' : `every ${n} months`;
      },
    ],
    [
      /\b(\d+|one|two|three|four|five|six)\s*(?:times?|x)\s*(?:a|per|each|\/)?\s*(day|daily|week|weekly|month|monthly)\b/i,
      (m) => timesPhrase(/^\d+$/.test(m[1]) ? +m[1] : NUMBER_WORDS[m[1].toLowerCase()], m[2]),
    ],
    [
      /\b(once|twice|thrice)\s*(?:a|per|each|\/)?\s*(day|daily|week|weekly|month|monthly)\b/i,
      (m) => timesPhrase(NUMBER_WORDS[m[1].toLowerCase()], m[2]),
    ],
    [/\bevery\s+morning\b/i, () => 'once a day in the morning'],
    [/\bevery\s+evening\b/i, () => 'once a day in the evening'],
    [/\bevery\s+(?:night|bedtime)\b|\bnightly\b|\bqhs\b|\bq\s*hs\b/i, () => 'once a day at bedtime'],
    [/\bevery\s+day\b|\bqd\b|\bq\.d\b|\bqday\b/i, () => 'once a day'],
    [/\bevery\s+week\b|\bweekly\b/i, () => 'once a week'],
    [/\bevery\s+month\b|\bmonthly\b/i, () => 'once a month'],
    [/\bbid\b|\bb\.i\.d\b/i, () => '2 times a day'],
    [/\btid\b|\bt\.i\.d\b/i, () => '3 times a day'],
    [/\bqid\b|\bq\.i\.d\b/i, () => '4 times a day'],
    [/\bqam\b/i, () => 'once a day in the morning'],
    [/\bqpm\b/i, () => 'once a day in the evening'],
    [/\bdaily\b/i, () => 'once a day'],
  ];
  for (const [re, build] of tests) {
    const m = re.exec(text);
    if (m) return { value: build(m), index: m.index, length: m[0].length };
  }
  return null;
}

const INSTRUCTIONS = [
  [/\bat\s+bedtime\b|\bbefore\s+bed\b/i, 'at bedtime'],
  [/\bin\s+the\s+morning\b|\beach\s+morning\b|\bin\s+the\s+am\b/i, 'in the morning'],
  [/\bin\s+the\s+evening\b|\beach\s+evening\b|\bin\s+the\s+pm\b/i, 'in the evening'],
  [/\bwith\s+(?:food|meals?|breakfast|lunch|dinner)\b/i, null],
  [/\bbefore\s+(?:each\s+)?(?:meals?|breakfast|lunch|dinner)\b/i, null],
  [/\bafter\s+(?:each\s+)?(?:meals?|breakfast|lunch|dinner)\b/i, null],
  [/\bon\s+an?\s+empty\s+stomach\b|\bin\s+empty\s+stomach\b|\bfasting\b/i, 'on an empty stomach'],
  [/\bas\s+directed\b/i, 'as directed'],
];

const normalizeStrength = (s) =>
  collapse(s)
    .replace(/\bmg\b/gi, 'mg')
    .replace(/(\d)\s*gm\b/gi, '$1 g')
    .replace(/\b(?:mcg|ug)\b|\u00B5g|\u03BCg/gi, 'mcg')
    .replace(/\bml\b/gi, 'mL')
    .replace(/\bmeq\b/gi, 'mEq')
    .replace(/\biu\b/gi, 'IU')
    .replace(/\bunits?\b/gi, (u) => u.toLowerCase())
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s*-\s*/g, '-');

const STATUS_WORDS = {
  active: 'taking', taking: 'taking', 'currently taking': 'taking',
  'not taking': 'not-taking', 'not-taking': 'not-taking', 'on hold': 'not-taking', held: 'not-taking',
  discontinued: 'discontinued', inactive: 'discontinued', stopped: 'discontinued',
  completed: 'discontinued', expired: 'discontinued',
};
const STATUS_TAG_RE =
  /\s*(?:[([]|[-;,]\s*)\s*(active|taking|currently taking|not taking|not-taking|discontinued|inactive|stopped|on hold|held|completed|expired)\s*[)\]]?\s*$/i;

const upperTrim = (v) => (typeof v === 'string' ? v.trim().toUpperCase() : v);

const findQty = (text) => {
  const re = new RegExp(QTY_RE.source, 'gi');
  let m;
  while ((m = re.exec(text))) {
    const prev = text[m.index - 1];
    if (prev === '/') continue; // part of a concentration like "mg/5 mL"
    return m;
  }
  return null;
};

function buildMedication(fields) {
  const name = upperTrim(fields.name || '');
  const formulation = upperTrim(fields.formulation || '');
  const route = fields.route || '';
  const timing = fields.timing !== undefined
    ? fields.timing
    : [route, fields.instructions || ''].filter(Boolean).join(', ');
  return {
    id: nextId(),
    name,
    formulation,
    dosage: fields.dosage || '',
    frequency: fields.frequency || '',
    timing,
    route,
    indication: fields.indication || '',
    status: fields.status || 'taking',
    isValidated: false,
    isValid: false,
    allowUnvalidated: true,
    companyName: '',
    medicationType: '',
    preventive: false,
    parsedName: fields.name || '',
    noteText: fields.noteText || '',
    sourceLine: fields.sourceLine || '',
    lineNumber: fields.lineNumber,
    bareName: !!fields.bareName,
  };
}

/** Template (pipe-delimited): Name | Dosage | Formulation | Frequency | Timing/Route | Indication | Status */
function parsePipeMedication(text, defaultStatus, entry) {
  const f = text.split('|').map((x) => collapse(x));
  if (!f[0]) return null;
  let status = defaultStatus;
  if (f[6]) {
    const key = f[6].toLowerCase().replace(/\s+/g, ' ');
    status = STATUS_WORDS[key] || defaultStatus;
  }
  const freq = f[3] ? (parseFrequency(f[3]) || {}).value || f[3] : '';
  return buildMedication({
    name: f[0],
    dosage: f[1] ? normalizeStrength(f[1]) : '',
    formulation: f[2] || '',
    frequency: freq,
    timing: f[4] || '',
    route: f[4] ? (ROUTES.map((r) => (r.re.test(f[4]) ? r.value : undefined)).find((v) => v) || '') : '',
    indication: f[5] || '',
    status,
    sourceLine: entry.text,
    lineNumber: entry.lineNumber,
  });
}

export function parseMedicationLine(rawText, defaultStatus = 'taking', entry = {}) {
  let text = collapse(rawText);
  if (text.includes('|')) {
    const piped = parsePipeMedication(text, defaultStatus, { text: rawText, lineNumber: entry.lineNumber });
    if (piped) return piped;
  }

  let status = defaultStatus;
  const tag = STATUS_TAG_RE.exec(text);
  if (tag) {
    status = STATUS_WORDS[tag[1].toLowerCase()] || status;
    text = text.slice(0, tag.index);
  }
  text = text.replace(/[;,]?\s*\bRx\b\s*$/i, '').trim();

  // "temazepam, Notes: as per pt ..." - the chart has no notes field, so the note is reported, not stored
  let noteText = '';
  text = text.replace(/\s*[;,]?\s*\bnotes?\s*:\s*(.+)$/i, (m, v) => {
    noteText = collapse(v);
    return ' ';
  });
  text = text.trim();

  let indication = '';
  text = text.replace(/\s*[;,-]?\s*\b(?:indication|dx|diagnosis|reason)\s*[:=-]\s*([^;|]+)/i, (m, v) => {
    indication = collapse(v);
    return ' ';
  });
  text = text.replace(/(?:^|\s)sig\s*:\s*/i, ' - ');
  text = collapse(text);
  if (!text) return null;

  // Mask letter-only parentheticals (generic names) so they stay part of the name.
  const masked = text.replace(/\(([A-Za-z][A-Za-z\s-]*)\)/g, (m) => 'x'.repeat(m.length));

  // Strength tokens
  const strengthRe = new RegExp(STRENGTH_SRC, 'gi');
  const strengthMatches = [];
  let sm;
  while ((sm = strengthRe.exec(masked))) {
    strengthMatches.push({ text: text.slice(sm.index, sm.index + sm[0].length), index: sm.index, length: sm[0].length });
  }

  // Sig start: earliest of verb, quantity, route word, frequency token or ';'
  const candidates = [];
  const verb = SIG_VERB_RE.exec(masked);
  if (verb) candidates.push(verb.index);
  // "tab(s)" has a letter-only parenthetical that the mask hides, so also look in the unmasked text
  const qty = findQty(masked) || findQty(text);
  if (qty) candidates.push(qty.index);
  const routeWord = SIG_ROUTE_RE.exec(masked);
  if (routeWord) candidates.push(routeWord.index);
  const semi = masked.indexOf(';');
  if (semi > -1) candidates.push(semi);
  // " - " starts the sig, unless it only separates the name from its form/strength
  // ("MiraLax - powder for reconstitution 17gm orally once a day")
  const dashSep = masked.search(/\s-\s/);
  if (dashSep > -1) {
    const afterDash = masked.slice(dashSep + 3);
    const formOrStrengthFollows =
      new RegExp(`^(?:${FORM_MODS}[\\s,]+)*(?:${FORM_WORDS})\\b`, 'i').test(afterDash) ||
      new RegExp(`^${STRENGTH_SRC}`, 'i').test(afterDash);
    if (!formOrStrengthFollows) candidates.push(dashSep);
  }
  // frequency tokens only count as the sig start if they are outside strength expressions
  let strengthBlanked = masked;
  strengthMatches.forEach((s) => {
    strengthBlanked = strengthBlanked.slice(0, s.index) + ' '.repeat(s.length) + strengthBlanked.slice(s.index + s.length);
  });
  const freqHit = parseFrequency(strengthBlanked);
  if (freqHit) candidates.push(freqHit.index);
  const sigStart = candidates.length ? Math.min(...candidates) : -1;

  const head = sigStart > -1 ? masked.slice(0, sigStart) : masked;
  const headOriginal = sigStart > -1 ? text.slice(0, sigStart) : text;
  // Form (first form expression before the sig)
  // "Bydureon Kit 2 mg powder for injection": "Kit" is part of the product name, so the real form wins
  let formulation = '';
  let formIdx = -1;
  let firstFormIdx = -1;
  const formRe = new RegExp(FORM_RE.source, 'gi');
  const formMatches = [];
  let fmm = formRe.exec(head);
  while (fmm) {
    formMatches.push(fmm);
    if (fmm[0].length === 0) formRe.lastIndex += 1;
    fmm = formRe.exec(head);
  }
  if (formMatches.length) {
    const fm = formMatches.find((f) => !/^kit\b/i.test(f[0].trim())) || formMatches[0];
    formulation = collapse(headOriginal.slice(fm.index, fm.index + fm[0].length));
    formIdx = fm.index;
    firstFormIdx = formMatches[0].index;
  }

  // Dosage: strengths before the first form word (if any), otherwise strengths in the head
  const headStrengths = strengthMatches.filter((s) => s.index + s.length <= head.length);
  const beforeForm = formIdx > -1 ? headStrengths.filter((s) => s.index < formIdx) : [];
  const dosageSource = beforeForm.length ? beforeForm : headStrengths;
  const dosageParts = [];
  const seenStrength = new Set();
  dosageSource.forEach((s) => {
    const norm = normalizeStrength(s.text);
    const key = norm.toLowerCase();
    if (!seenStrength.has(key)) {
      seenStrength.add(key);
      dosageParts.push(norm);
    }
  });
  const dosage = dosageParts.join(', ');

  // Name: up to the earliest of digit token / form word / separator / sig
  const nameEndCandidates = [head.length];
  const digitTok = head.search(/\s\d/);
  if (digitTok > -1) nameEndCandidates.push(digitTok);
  const comma = head.search(/[,;(]/);
  if (comma > -1) nameEndCandidates.push(comma);
  const dash = head.search(/\s-\s/);
  if (dash > -1) nameEndCandidates.push(dash);
  if (firstFormIdx > -1) nameEndCandidates.push(firstFormIdx);
  const nameEnd = Math.min(...nameEndCandidates);
  let name = collapse(headOriginal.slice(0, nameEnd)).replace(/[-\s,:;]+$/, '');
  // trailing form modifiers that ended up in the name (e.g. "Lisinopril oral")
  name = name.replace(/\s+(?:oral|topical|ophthalmic|nasal|transdermal)$/i, '');
  if (!name || name.length < 2 || !hasLetters(name)) return null;

  // Sig analysis (strength tokens blanked out). Uses the unmasked text so that sig words inside
  // parentheses, e.g. "once a day (in the evening)", are still seen.
  let sigBlanked = text;
  strengthMatches.forEach((s) => {
    sigBlanked = sigBlanked.slice(0, s.index) + ' '.repeat(s.length) + sigBlanked.slice(s.index + s.length);
  });
  const searchText = collapse(sigBlanked);

  let route = '';
  for (const r of ROUTES) {
    const rm = r.re.exec(searchText);
    if (rm) {
      route = r.value || collapse(rm[0]).toLowerCase();
      break;
    }
  }

  let frequency = freqHit ? freqHit.value : '';
  const prn = /\bprn\b|\bas\s+needed\b|\bsos\b/i.test(searchText);

  const instructions = [];
  INSTRUCTIONS.forEach(([re, canonical]) => {
    const im = re.exec(searchText);
    if (im) instructions.push(canonical || collapse(im[0]).toLowerCase());
  });
  // An amount after the form that differs from the strength is what is actually given per dose
  // ("Lantus 100 unit/mL solution 20 units subcutaneously" -> 20 units; "250 mg/5 mL suspension 5 mL").
  if (beforeForm.length) {
    headStrengths
      .filter((s) => s.index > formIdx)
      .forEach((s) => {
        const amount = normalizeStrength(s.text);
        if (!seenStrength.has(amount.toLowerCase()) && !instructions.includes(amount)) instructions.unshift(amount);
      });
  }

  // Quantity per dose matters ("2 tab(s)" of a 400 mg tablet is 800 mg). "1 tablet/capsule" is implied.
  if (qty) {
    const qm = /^(\d+(?:\/\d+)?(?:\.\d+)?)\s*([a-z]+)/i.exec(qty[0]);
    if (qm) {
      const [numerator, denominator] = qm[1].split('/');
      const amount = denominator ? parseFloat(numerator) / parseFloat(denominator) : parseFloat(numerator);
      const u = qm[2].toLowerCase();
      const base = /^tab/.test(u) ? 'tablet' : /^cap/.test(u) ? 'capsule' : /^(?:drop|gtt)/.test(u) ? 'drop'
        : /^puff/.test(u) ? 'puff' : /^spray/.test(u) ? 'spray' : /^patch/.test(u) ? 'patch' : 'application';
      const plural = amount > 1 ? (base === 'patch' ? 'patches' : `${base}s`) : base;
      if (!(amount === 1 && (base === 'tablet' || base === 'capsule'))) {
        instructions.unshift(`${qm[1]} ${plural}`);
      }
    }
  }
  const dedupedInstr = [];
  instructions.forEach((i) => {
    if (frequency && frequency.toLowerCase().includes(i.toLowerCase())) return;
    if (!dedupedInstr.includes(i)) dedupedInstr.push(i);
  });
  if (prn) {
    if (!frequency) frequency = 'as needed';
    else if (!dedupedInstr.includes('as needed')) dedupedInstr.push('as needed');
  }

  if (!indication) {
    const forMatch =
      /\bfor\s+(?!\d)([A-Za-z][A-Za-z\s/'-]{2,60}?)(?=\s*(?:$|[.;,(]|\s-\s|\s(?:and|then|with|at|in|every|daily|twice|once|prn)\b))/i.exec(text);
    if (
      forMatch &&
      !/^(?:the\s+)?(?:next|first|last)\b|\b(?:days?|weeks?|months?)\b/i.test(forMatch[1]) &&
      !/^(?:injection|injectable|infusion|reconstitution|inhalation|nebulization|oral|topical|ophthalmic|nasal|external|use)\b/i.test(forMatch[1])
    ) {
      indication = collapse(forMatch[1]);
    }
  }

  const signals =
    (dosage ? 1 : 0) + (formulation ? 1 : 0) + (frequency ? 1 : 0) + (route ? 1 : 0) + (verb || qty ? 1 : 0);
  const bareName = signals === 0;
  if (bareName && !(/^[A-Za-z][A-Za-z\s\-']{1,40}$/.test(text) && text.split(' ').length <= 4)) {
    return null;
  }

  return buildMedication({
    name,
    formulation,
    dosage,
    frequency,
    route,
    instructions: dedupedInstr.join(', '),
    indication,
    status,
    noteText,
    sourceLine: rawText,
    lineNumber: entry.lineNumber,
    bareName,
  });
}

const CONTINUATION_RE =
  /^(?:(?:take|takes|inject|apply|use|instill|inhale|place|insert|chew|dissolve|swallow|give|administer|by mouth|orally|every|once|twice|daily|at bedtime|for|with|as needed|prn)\b|(?:sig|indication|dx|notes?)\s*:)/i;

function parseMedications(entries, ctx) {
  const merged = [];
  entries.forEach((e) => {
    if (e.kind === 'status') {
      merged.push(e);
      if (e.remainder) merged.push({ text: e.remainder, lineNumber: e.lineNumber });
      return;
    }
    const last = merged[merged.length - 1];
    if (last && !last.kind && CONTINUATION_RE.test(e.text) && !/^\s*\d/.test(e.text)) {
      last.text = `${last.text} ${e.text}`;
      return;
    }
    merged.push({ ...e });
  });

  let status = 'taking';
  const items = [];
  const seen = new Set();
  merged.forEach((e) => {
    if (e.kind === 'status') {
      status = e.status;
      return;
    }
    const text = e.text;
    if (isColumnHeaderLine(text)) return;
    if (/^(?:medication|current|list|reviewed|reconciled|medication list|no (?:known )?(?:medications|meds))\b/i.test(text) && !/\d/.test(text)) {
      return;
    }
    const med = parseMedicationLine(text, status, e);
    if (!med) {
      ctx.unrecognized(e, 'Could not identify a medication name on this line');
      return;
    }
    if (med.noteText) {
      ctx.warn(e, `Note not stored (the chart has no notes field): "${med.noteText}"`);
    }
    const key = [med.name, med.dosage, med.formulation, med.frequency, med.status].join('|').toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    items.push(med);
  });
  return items;
}

/* -------------------------------------------------------------------------- */
/* Date-bearing list sections                                                 */
/* -------------------------------------------------------------------------- */

function splitPipe(text) {
  return text.split('|').map((x) => collapse(x));
}

function extractDateField(text, formatter, opts, ctx, entry) {
  const dt = findDate(text, opts);
  if (!dt) return { date: '', rest: text };
  if (dt.approx) {
    ctx.warn(entry, `"${text.slice(dt.index, dt.index + dt.length)}" was read as month/year`);
  }
  return { date: formatter(dt), rest: trimDelims(removeSpan(text, dt)) };
}

const DATE_OPTS = { twoPart: true, yearOnly: true };

function firstColonIndex(text) {
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === ':' && !(/\d/.test(text[i - 1] || '') && /\d/.test(text[i + 1] || ''))) return i;
  }
  return -1;
}

/**
 * Splits one entry into its fields at commas and at spaced dashes ("a, b - c", also "a -- b").
 * Not split: delimiters inside (...) or [...], thousands separators (1,000) and hyphens inside words
 * ("Follow-up", "COVID-19"). Empty parts are dropped, so a removed date or name leaves no blank field.
 */
export function splitFields(text) {
  return scanFields(text).map((p) => p.value);
}

/** Like splitFields, but keeps where each field starts so a tail can be taken verbatim (see fieldsTail). */
function scanFields(text) {
  const pieces = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '(' || ch === '[') {
      depth += 1;
    } else if ((ch === ')' || ch === ']') && depth > 0) {
      depth -= 1;
    } else if (depth === 0) {
      if (ch === ',' && !(/\d/.test(text[i - 1] || '') && /^\d{3}(?!\d)/.test(text.slice(i + 1)))) {
        pieces.push({ raw: text.slice(start, i), start });
        start = i + 1;
      } else if (ch === '-' && (i === 0 || /\s/.test(text[i - 1]))) {
        const dashes = /^-{1,2}(?=\s)/.exec(text.slice(i));
        if (dashes) {
          pieces.push({ raw: text.slice(start, i), start });
          start = i + dashes[0].length;
          i += dashes[0].length - 1;
        }
      }
    }
  }
  pieces.push({ raw: text.slice(start), start });
  return pieces
    .map((p) => ({ value: trimDelims(p.raw), start: p.start }))
    .filter((p) => p.value);
}

/** Everything from field `index` onwards exactly as written (keeps the original commas / dashes). */
const fieldsTail = (text, fields, index) => trimDelims(text.slice(fields[index].start));

function parseSurgicalOrHospital(entries, field, ctx) {
  const items = [];
  entries.forEach((e) => {
    if (isColumnHeaderLine(e.text) || !hasLetters(e.text)) return;
    let date = '';
    let value = '';
    if (e.text.includes('|')) {
      const f = splitPipe(e.text);
      const d = f[0] ? findDate(f[0], DATE_OPTS) : null;
      if (d) {
        date = formatDateMoYr(d);
        value = f.slice(1).filter(Boolean).join(' - ');
      } else {
        value = f.filter(Boolean).join(' - ');
      }
    } else {
      const r = extractDateField(e.text, formatDateMoYr, DATE_OPTS, ctx, e);
      date = r.date;
      value = r.rest;
    }
    if (!value) {
      ctx.unrecognized(e, 'No description found on this line');
      return;
    }
    if (!date) {
      ctx.warn(e, 'No date found; this entry will be added without a date');
    }
    items.push({ id: nextId(), date, [field]: value, sourceLine: e.text, lineNumber: e.lineNumber });
  });
  return items;
}

function parseProcedures(entries, ctx) {
  const items = [];
  entries.forEach((e) => {
    if (isColumnHeaderLine(e.text) || !hasLetters(e.text)) return;
    let date = '';
    let name = '';
    let indication = '';
    let result = '';
    if (e.text.includes('|')) {
      const f = splitPipe(e.text);
      const d = f[0] ? findDate(f[0], DATE_OPTS) : null;
      const offset = d ? 1 : 0;
      if (d) date = formatDateFull(d);
      name = f[offset] || '';
      indication = f[offset + 1] || '';
      result = f[offset + 2] || '';
    } else {
      const r = extractDateField(e.text, formatDateFull, DATE_OPTS, ctx, e);
      date = r.date;
      const fields = scanFields(r.rest);
      const parts = fields.map((f) => f.value);
      const idx = firstColonIndex(r.rest);
      if (idx > -1 && (parts.length === 0 || firstColonIndex(parts[0]) > -1)) {
        // "Procedure: result"
        name = trimDelims(r.rest.slice(0, idx));
        result = trimDelims(r.rest.slice(idx + 1));
      } else if (parts.length >= 3) {
        // "procedure, indication, result" (or separated by " - "); the result keeps its own commas
        [name, indication] = parts;
        result = fieldsTail(r.rest, fields, 2);
      } else if (parts.length === 2) {
        [name, result] = parts;
        ctx.warn(e, 'Two parts found; read as procedure and result. Write "procedure, indication, result" to also add an indication');
      } else {
        name = parts[0] || '';
      }
    }
    if (!name) {
      ctx.unrecognized(e, 'No procedure name found on this line');
      return;
    }
    items.push({ id: nextId(), date, name, indication, result, sourceLine: e.text, lineNumber: e.lineNumber });
  });
  return items;
}

function parseImaging(entries, ctx) {
  const items = [];
  entries.forEach((e) => {
    if (isColumnHeaderLine(e.text) || !hasLetters(e.text)) return;
    let date = '';
    let locationContrast = '';
    let indication = '';
    let result = '';
    if (e.text.includes('|')) {
      const f = splitPipe(e.text);
      const d = f[0] ? findDate(f[0], DATE_OPTS) : null;
      const offset = d ? 1 : 0;
      if (d) date = formatDateFull(d);
      locationContrast = f[offset] || '';
      indication = f[offset + 1] || '';
      result = f[offset + 2] || '';
    } else {
      const r = extractDateField(e.text, formatDateFull, DATE_OPTS, ctx, e);
      date = r.date;
      const fields = scanFields(r.rest);
      const parts = fields.map((f) => f.value);
      const idx = firstColonIndex(r.rest);
      if (idx > -1 && (parts.length === 0 || firstColonIndex(parts[0]) > -1)) {
        // "Study: result"
        locationContrast = trimDelims(r.rest.slice(0, idx));
        result = trimDelims(r.rest.slice(idx + 1));
      } else if (parts.length >= 3) {
        // "study, indication, result" (or separated by " - "); the result keeps its own commas
        [locationContrast, indication] = parts;
        result = fieldsTail(r.rest, fields, 2);
      } else if (parts.length === 2) {
        [locationContrast, result] = parts;
        ctx.warn(e, 'Two parts found; read as study and result. Write "study, indication, result" to also add an indication');
      } else {
        locationContrast = parts[0] || '';
      }
    }
    if (!locationContrast && !indication && !result) {
      ctx.unrecognized(e, 'No imaging details found on this line');
      return;
    }
    items.push({
      id: nextId(), date, locationContrast, indication, result, sourceLine: e.text, lineNumber: e.lineNumber,
    });
  });
  return items;
}

function parseSpecialists(entries, ctx) {
  const items = [];
  entries.forEach((e) => {
    if (isColumnHeaderLine(e.text) || !hasLetters(e.text)) return;
    let date = '';
    let specialty = '';
    let specialistName = '';
    let details = '';
    if (e.text.includes('|')) {
      const f = splitPipe(e.text);
      const d = f[0] ? findDate(f[0], DATE_OPTS) : null;
      const offset = d ? 1 : 0;
      if (d) date = formatDateFull(d);
      specialty = f[offset] || '';
      specialistName = f[offset + 1] || '';
      details = f[offset + 2] || '';
    } else {
      const r = extractDateField(e.text, formatDateFull, DATE_OPTS, ctx, e);
      date = r.date;
      let rest = r.rest;
      // "Dr. Smith" or "Dr. John Smith" (two tokens only when followed by a delimiter/end)
      const drMatch =
        /\bDr\.?\s+([A-Z][A-Za-z'-]+\s+[A-Z][A-Za-z'-]+)(?=\s*(?:$|[,;:()-]))/.exec(rest) ||
        /\bDr\.?\s+([A-Z][A-Za-z'-]+)/.exec(rest);
      const credMatch = /\b([A-Z][A-Za-z'-]+),?\s+(?:MD|M\.D\.|DO|D\.O\.|NP|PA-C|PA)\b/.exec(rest);
      if (drMatch) {
        specialistName = `Dr. ${drMatch[1]}`;
        rest = rest.slice(0, drMatch.index) + ' ' + rest.slice(drMatch.index + drMatch[0].length);
      } else if (credMatch) {
        specialistName = credMatch[1];
        rest = rest.slice(0, credMatch.index) + ' ' + rest.slice(credMatch.index + credMatch[0].length);
      }
      rest = trimDelims(rest);
      const fields = scanFields(rest);
      const parts = fields.map((f) => f.value);
      const idx = firstColonIndex(rest);
      if (idx > -1 && (parts.length === 0 || firstColonIndex(parts[0]) > -1)) {
        // "Specialty: details"
        specialty = trimDelims(rest.slice(0, idx));
        details = trimDelims(rest.slice(idx + 1));
      } else if (parts.length >= 2) {
        // "specialty, details" / "specialty - details"; the specialist ("Dr. Smith") was already taken out
        [specialty] = parts;
        details = fieldsTail(rest, fields, 1);
      } else if (parts.length === 1 && parts[0].split(' ').length <= 4) {
        [specialty] = parts;
      } else if (parts.length === 1) {
        [details] = parts;
        ctx.warn(e, 'Could not separate the specialty from the details; the full text was placed in Details. Write "specialty, Dr. Name, details"');
      }
    }
    if (!specialty && !specialistName && !details) {
      ctx.unrecognized(e, 'No specialist visit details found on this line');
      return;
    }
    items.push({
      id: nextId(), date, specialty, specialistName, details, sourceLine: e.text, lineNumber: e.lineNumber,
    });
  });
  return items;
}

/* -------------------------------------------------------------------------- */
/* Vitals & labs                                                              */
/* -------------------------------------------------------------------------- */

const pairObj = () => ({ before: '', after: '' });

export const createEmptyVitalsLabs = () => ({
  bloodPressure: { systolic: '', diastolic: '' },
  weight: pairObj(),
  bmi: pairObj(),
  weightLoss: { value: '', duration: '', durationUnit: 'months' },
  glucose: { a1c: pairObj(), fbs: pairObj() },
  kidney: { srCreat: pairObj(), egfr: '', urineAlbCreatRatio: pairObj() },
  liver: { ast: pairObj(), alt: pairObj(), totProt: '', totBilirubin: '', srAlb: '', alp: '' },
  electrolytes: { na: '', k: '', cl: '', co2: '', ca: pairObj() },
  lipidPanel: { totChol: pairObj(), ldl: pairObj(), hdl: pairObj(), tg: pairObj() },
  cbc: { hb: pairObj(), hematocrit: '', rbc: '', mcv: '', wbc: '', platelets: '' },
  thyroid: { tsh: pairObj(), freeT3: pairObj(), freeT4: pairObj() },
  parathyroid: { intactPTH: pairObj(), wholePTH: pairObj(), srPhosphorous: pairObj() },
  ra: { rf: '', esr: '', crp: '', ana: '' },
});

const setByPath = (obj, path, value) => {
  let cur = obj;
  for (let i = 0; i < path.length - 1; i += 1) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
};

const normUnit = (u) =>
  String(u)
    .toLowerCase()
    .replace(/[\u00B5\u03BC]/g, 'u')
    .replace(/\u00B2/g, '2')
    .replace(/[\s*^.]/g, '');

const MGDL = { 'mg/dl': 1 };
const ML_MIN = { 'ml/min/173m2': 1, 'ml/min/173': 1, 'ml/min': 1 };
const COUNT_PER_UL = {
  '/ul': 1, 'cells/ul': 1, '/mm3': 1, 'cells/mm3': 1,
  'k/ul': 1000, 'x10e3/ul': 1000, '10e3/ul': 1000, '103/ul': 1000, 'x103/ul': 1000, 'thou/ul': 1000,
  'k/mm3': 1000, 'x10e3/mm3': 1000, '103/mm3': 1000, 'x10e9/l': 1000, '10e9/l': 1000, '109/l': 1000,
};

/**
 * Tracked tests. `kind`: pair = {before, after}; single = one value; text = free text.
 * `units`: accepted units mapped to a conversion factor into the chart's unit.
 * `range`: typical range of the chart input (used for warnings only).
 */
const TESTS = [
  { id: 'weightLoss', special: 'weightLoss', label: 'Weight loss', pattern: /^weight\s+loss\b/i },
  {
    id: 'weight', label: 'Weight (lbs)', path: ['weight'], kind: 'pair', decimals: 1,
    pattern: /^(?:weight|wt)\b/i, units: { lb: 1, lbs: 1, pound: 1, pounds: 1, kg: 2.20462, kgs: 2.20462 }, range: [50, 500],
  },
  {
    id: 'bmi', label: 'BMI', path: ['bmi'], kind: 'pair', decimals: 1,
    pattern: /^(?:bmi|body\s+mass\s+index)\b/i, units: { 'kg/m2': 1, 'kg/m': 1 }, range: [10, 100],
  },
  { id: 'bp', special: 'bp', label: 'Blood pressure', pattern: /^(?:bp|blood\s+pressure)\b/i },
  {
    id: 'a1c', label: 'A1c (%)', path: ['glucose', 'a1c'], kind: 'pair', decimals: 2,
    pattern: /^(?:hb\s?a1c|hgb\s?a1c|hemoglobin\s+a1c|haemoglobin\s+a1c|glycated\s+hemoglobin|glycosylated\s+hemoglobin|a1c|a1-c)\b/i,
    units: { '%': 1 }, range: [3, 15],
  },
  {
    id: 'fbs', label: 'FBS (mg/dL)', path: ['glucose', 'fbs'], kind: 'pair', decimals: 1,
    pattern: /^(?:fbs|fpg|fasting\s+(?:blood\s+)?(?:glucose|sugar)|fasting\s+plasma\s+glucose|glucose,?\s*fasting)\b/i,
    units: MGDL, range: [50, 500],
  },
  {
    id: 'uacr', label: 'Urine Alb:Creat ratio (mg/g)', path: ['kidney', 'urineAlbCreatRatio'], kind: 'pair', decimals: 1,
    pattern: /^(?:uacr|(?:urine\s+)?(?:micro)?albumin\s*(?:\/|:|-|to)\s*creat(?:inine)?(?:\s+ratio)?(?:,?\s*urine)?|albumin[\s-]*creatinine\s+ratio(?:,?\s*urine)?)\b/i,
    units: { 'mg/g': 1, 'mg/gcreat': 1, 'mg/gcr': 1, 'mg/gcreatinine': 1, 'ug/mg': 1, 'mcg/mg': 1 }, range: [0, 1000],
  },
  {
    id: 'srCreat', label: 'Sr. Creatinine (mg/dL)', path: ['kidney', 'srCreat'], kind: 'pair', decimals: 2,
    pattern: /^(?:serum\s+creatinine|creatinine(?:,?\s*serum)?|sr\.?\s*creat(?:inine)?|creat)\b(?!\s+clearance|,?\s*urine)/i,
    units: { ...MGDL, 'umol/l': 1 / 88.4 }, range: [0.1, 20],
  },
  {
    id: 'egfr', label: 'eGFR', path: ['kidney', 'egfr'], kind: 'single', decimals: 1,
    pattern: /^(?:e?gfr|estimated\s+gfr|glomerular\s+filtration\s+rate)\b/i,
    units: ML_MIN, range: [5, 200],
  },
  {
    id: 'ast', label: 'AST (U/L)', path: ['liver', 'ast'], kind: 'pair', decimals: 1,
    pattern: /^(?:ast|sgot|aspartate\s+(?:amino)?transferase)\b/i, units: { 'u/l': 1, 'iu/l': 1 }, range: [0, 1000],
  },
  {
    id: 'alt', label: 'ALT (U/L)', path: ['liver', 'alt'], kind: 'pair', decimals: 1,
    pattern: /^(?:alt|sgpt|alanine\s+(?:amino)?transferase)\b/i, units: { 'u/l': 1, 'iu/l': 1 }, range: [0, 1000],
  },
  {
    id: 'totProt', label: 'Total protein (g/dL)', path: ['liver', 'totProt'], kind: 'single', decimals: 2,
    pattern: /^(?:total\s+protein|protein,?\s*total|tot\.?\s*prot)\b/i, units: { 'g/dl': 1, 'g/l': 0.1 }, range: [0, 20],
  },
  {
    id: 'totBilirubin', label: 'Total bilirubin (mg/dL)', path: ['liver', 'totBilirubin'], kind: 'single', decimals: 2,
    pattern: /^(?:total\s+bilirubin|bilirubin,?\s*total|tot\.?\s*bili(?:rubin)?)\b/i, units: { ...MGDL, 'umol/l': 1 / 17.1 }, range: [0, 50],
  },
  {
    id: 'srAlb', label: 'Serum albumin (g/dL)', path: ['liver', 'srAlb'], kind: 'single', decimals: 2,
    pattern: /^(?:serum\s+albumin|albumin(?:,?\s*serum)?|sr\.?\s*alb)\b(?!\s*[/:-]\s*(?:glob|creat))/i, units: { 'g/dl': 1, 'g/l': 0.1 }, range: [0, 10],
  },
  {
    id: 'alp', label: 'ALP (U/L)', path: ['liver', 'alp'], kind: 'single', decimals: 1,
    pattern: /^(?:alp|alk\.?\s*phos(?:phatase)?|alkaline\s+phosphatase)\b/i, units: { 'u/l': 1, 'iu/l': 1 }, range: [0, 1000],
  },
  {
    id: 'na', label: 'Na+ (mEq/L)', path: ['electrolytes', 'na'], kind: 'single', decimals: 1,
    pattern: /^(?:sodium\b(?!\s+(?:bicarbonate|chloride))|na\s*\+?(?=\s*:|\s+\d))/i, units: { 'meq/l': 1, 'mmol/l': 1 }, range: [100, 200],
  },
  {
    id: 'k', label: 'K+ (mEq/L)', path: ['electrolytes', 'k'], kind: 'single', decimals: 2,
    pattern: /^(?:potassium\b|k\s*\+?(?=\s*:|\s+\d))/i, units: { 'meq/l': 1, 'mmol/l': 1 }, range: [1, 10],
  },
  {
    id: 'cl', label: 'Cl- (mEq/L)', path: ['electrolytes', 'cl'], kind: 'single', decimals: 1,
    pattern: /^(?:chloride\b|cl\s*-?(?=\s*:|\s+\d))/i, units: { 'meq/l': 1, 'mmol/l': 1 }, range: [80, 150],
  },
  {
    id: 'co2', label: 'CO2 (mEq/L)', path: ['electrolytes', 'co2'], kind: 'single', decimals: 1,
    pattern: /^(?:co2(?:,?\s*total)?|carbon\s+dioxide(?:,?\s*total)?|bicarbonate|hco3-?)\b/i, units: { 'meq/l': 1, 'mmol/l': 1 }, range: [10, 50],
  },
  {
    id: 'ca', label: 'Calcium (mg/dL)', path: ['electrolytes', 'ca'], kind: 'pair', decimals: 2,
    pattern: /^(?:calcium\b(?!,?\s*ionized)(?:,?\s*(?:total|serum)\b)?|ca\s*(?:\+\+|\+2|2\+|\+)(?=\s*:|\s+\d)|ca(?=\s*:))/i,
    units: { ...MGDL, 'mmol/l': 4.008 }, range: [5, 15],
  },
  {
    id: 'ldl', label: 'LDL (mg/dL)', path: ['lipidPanel', 'ldl'], kind: 'pair', decimals: 1,
    pattern: /^(?:ldl(?:[-\s]?c)?(?:\s+cholesterol)?(?:\s*(?:calc(?:ulated)?|direct))?|cholesterol,?\s*ldl|low\s+density\s+lipoprotein)\b/i,
    units: { ...MGDL, 'mmol/l': 38.67 }, range: [20, 300],
  },
  {
    id: 'hdl', label: 'HDL (mg/dL)', path: ['lipidPanel', 'hdl'], kind: 'pair', decimals: 1,
    pattern: /^(?:hdl(?:[-\s]?c)?(?:\s+cholesterol)?|cholesterol,?\s*hdl|high\s+density\s+lipoprotein)\b/i,
    units: { ...MGDL, 'mmol/l': 38.67 }, range: [10, 150],
  },
  {
    id: 'tg', label: 'TG (mg/dL)', path: ['lipidPanel', 'tg'], kind: 'pair', decimals: 1,
    pattern: /^(?:tg|triglycerides?|trigs?)\b/i, units: { ...MGDL, 'mmol/l': 88.57 }, range: [20, 1000],
  },
  {
    id: 'totChol', label: 'Total cholesterol (mg/dL)', path: ['lipidPanel', 'totChol'], kind: 'pair', decimals: 1,
    pattern: /^(?:total\s+cholesterol|cholesterol,?\s*total|tot\.?\s*chol(?:esterol)?|cholesterol(?!,?\s*(?:ldl|hdl|non|vldl)))\b/i,
    units: { ...MGDL, 'mmol/l': 38.67 }, range: [50, 500],
  },
  {
    id: 'hb', label: 'Hb (g/dL)', path: ['cbc', 'hb'], kind: 'pair', decimals: 1,
    pattern: /^(?:hemoglobin|haemoglobin|hgb|hb)\b(?!\s*a1c)/i, units: { 'g/dl': 1, 'g/l': 0.1 }, range: [5, 25],
  },
  {
    id: 'hematocrit', label: 'Hematocrit (%)', path: ['cbc', 'hematocrit'], kind: 'single', decimals: 1,
    pattern: /^(?:hematocrit|hct)\b/i, units: { '%': 1 }, range: [15, 70],
  },
  {
    id: 'rbc', label: 'RBC (M/uL)', path: ['cbc', 'rbc'], kind: 'single', decimals: 2,
    pattern: /^(?:rbc|red\s+blood\s+(?:cell|cells)(?:\s+count)?|red\s+cell\s+count)\b/i,
    units: {
      'm/ul': 1, 'x10e6/ul': 1, '10e6/ul': 1, '106/ul': 1, 'x106/ul': 1, 'mil/ul': 1,
      'million/ul': 1, 'mill/ul': 1, 'x10e12/l': 1, '10e12/l': 1, '1012/l': 1, 't/l': 1,
    },
    range: [2, 8],
  },
  {
    id: 'mcv', label: 'MCV (fL)', path: ['cbc', 'mcv'], kind: 'single', decimals: 1,
    pattern: /^(?:mcv|mean\s+corpuscular\s+volume)\b/i, units: { fl: 1 }, range: [50, 150],
  },
  {
    id: 'wbc', label: 'WBC (/uL)', path: ['cbc', 'wbc'], kind: 'single', decimals: 0, scaleIfBelow: 100,
    pattern: /^(?:wbc|white\s+blood\s+(?:cell|cells)(?:\s+count)?|white\s+cell\s+count|leukocytes?)\b/i,
    units: COUNT_PER_UL, range: [1000, 50000],
  },
  {
    id: 'platelets', label: 'Platelets (/uL)', path: ['cbc', 'platelets'], kind: 'single', decimals: 0, scaleIfBelow: 2000,
    pattern: /^(?:platelets?(?:\s+count)?|plt)\b/i, units: COUNT_PER_UL, range: [50000, 1000000],
  },
  {
    id: 'freeT3', label: 'Free T3 (pg/mL)', path: ['thyroid', 'freeT3'], kind: 'pair', decimals: 2,
    pattern: /^(?:free\s+t3|ft3|t3,?\s*free|triiodothyronine,?\s*free)\b/i,
    units: { 'pg/ml': 1, 'ng/dl': 10, 'pmol/l': 0.651 }, range: [0, 20],
  },
  {
    id: 'freeT4', label: 'Free T4 (ng/dL)', path: ['thyroid', 'freeT4'], kind: 'pair', decimals: 2,
    pattern: /^(?:free\s+t4|ft4|t4,?\s*free|thyroxine,?\s*free|free\s+thyroxine)\b/i,
    units: { 'ng/dl': 1, 'pmol/l': 0.0777 }, range: [0, 10],
  },
  {
    id: 'tsh', label: 'TSH (mIU/L)', path: ['thyroid', 'tsh'], kind: 'pair', decimals: 3,
    pattern: /^(?:tsh|thyroid\s+stimulating\s+hormone|thyrotropin)\b/i,
    units: { 'miu/l': 1, 'uiu/ml': 1 }, range: [0, 100],
  },
  {
    id: 'wholePTH', label: 'Whole PTH (pg/mL)', path: ['parathyroid', 'wholePTH'], kind: 'pair', decimals: 1,
    pattern: /^(?:whole\s+pth|pth,?\s*whole|wpth|bio-?intact\s+pth)\b/i,
    units: { 'pg/ml': 1, 'ng/l': 1 }, range: [0, 500],
  },
  {
    id: 'intactPTH', label: 'Intact/Active PTH (pg/mL)', path: ['parathyroid', 'intactPTH'], kind: 'pair', decimals: 1,
    pattern: /^(?:intact\s+pth|pth,?\s*intact|ipth|(?:intact|active)\s*\/\s*(?:active|intact)\s+pth|active\s+pth|pth,?\s*active)\b/i,
    units: { 'pg/ml': 1, 'ng/l': 1 }, range: [0, 500],
  },
  {
    id: 'srPhosphorous', label: 'Sr. Phosphorous (mg/dL)', path: ['parathyroid', 'srPhosphorous'], kind: 'pair', decimals: 2,
    pattern: /^(?:(?:sr\.?\s*|serum\s+|inorganic\s+)?phosphor(?:us|ous)|phosphate)\b/i,
    units: { ...MGDL, 'mmol/l': 3.097 }, range: [0, 20],
  },
  {
    id: 'rf', label: 'RF (IU/mL)', path: ['ra', 'rf'], kind: 'single', decimals: 1,
    pattern: /^(?:rf|rheumatoid\s+factor)\b/i, units: { 'iu/ml': 1, 'u/ml': 1 }, range: [0, 1000],
  },
  {
    id: 'esr', label: 'ESR (mm/hr)', path: ['ra', 'esr'], kind: 'single', decimals: 0,
    pattern: /^(?:esr|sed(?:imentation)?\s+rate|erythrocyte\s+sedimentation\s+rate)\b/i,
    units: { 'mm/hr': 1, 'mm/h': 1, 'mm/hour': 1 }, range: [0, 200],
  },
  {
    id: 'crp', label: 'CRP (mg/L)', path: ['ra', 'crp'], kind: 'single', decimals: 2,
    pattern: /^(?:crp|c[-\s]?reactive\s+protein)\b(?!\s*,?\s*(?:high|hs))/i,
    units: { 'mg/l': 1, 'mg/dl': 10 }, range: [0, 100],
  },
  {
    id: 'ana', label: 'ANA', path: ['ra', 'ana'], kind: 'text',
    pattern: /^(?:ana|antinuclear\s+antibod(?:y|ies))\b/i,
  },
];

// Recognised vitals/labs that the chart does not track (reported as "skipped", never silently lost)
const UNTRACKED_RE =
  /^(?:pulse|heart\s+rate|hr|temp(?:erature)?|spo2|o2\s+sat(?:uration)?|oxygen\s+sat(?:uration)?|resp(?:iratory)?(?:\s+rate)?|rr|height|ht|pain(?:\s+score)?|vision|visual\s+acuity|waist(?:\s+circumference)?|head\s+circumference|bsa|glucose|random\s+glucose|non[-\s]?hdl(?:\s+cholesterol)?|vldl|bun|urea\s+nitrogen|magnesium|mg|vitamin\s+\w+|ferritin|iron|tibc|uric\s+acid|globulin|a\/g\s+ratio|hs[-\s]?crp|pth|parathyroid\s+hormone|ionized\s+calcium|calcium,?\s*ionized|neutrophils?|lymphocytes?|monocytes?|eosinophils?|basophils?|mch|mchc|rdw|mpv|psa|inr|pt|ptt|troponin|bnp|nt[-\s]?probnp|ldh|lipase|amylase|cpk|ck)\b/i;

const FLAG_WORDS = /^(?:h|l|hh|ll|a|aa|n|high|low|abnormal|normal|critical|crit|flag)$/i;

const UNIT_RE =
  /^\s*(%|(?:x\s*)?10\s*[*^eE]\s*\d+\s*\/\s*[A-Za-z\u00B5\u03BC]+|\/\s*[A-Za-z\u00B5\u03BC]+\d*|[A-Za-z\u00B5\u03BC][A-Za-z\u00B5\u03BC0-9.\u00B2]*(?:\s*\/\s*[A-Za-z\u00B5\u03BC0-9.\u00B2]+)*)/;

const looksLikeUnit = (n) =>
  /\//.test(n) || n === '%' || /^(?:mg|g|kg|lb|lbs|mmol|umol|iu|fl|pg|ng|ml|l|mm|oz|st)$/.test(n);

const roundTo = (n, d) => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};

const matchTest = (text) => {
  for (const t of TESTS) {
    const m = t.pattern.exec(text);
    if (m) return { test: t, length: m[0].length };
  }
  return null;
};

const startsWithUntracked = (text) => {
  const m = UNTRACKED_RE.exec(text);
  return m ? m[0] : null;
};

/** Reads the first numeric reading in `seg` and converts it using the test's unit table. */
function readNumeric(seg, test) {
  const stripped = seg.replace(/\([^)]*\)/g, ' ');
  const src = /\d/.test(stripped) ? stripped : seg.replace(/[()]/g, ' ');
  const m = /([<>\u2264\u2265]=?)?\s*(\d+(?:\.\d+)?)/.exec(src);
  if (!m) return { ok: false, reason: 'No numeric result found' };

  const raw = m[2];
  const comparator = m[1] || '';
  const after = src.slice(m.index + m[0].length);
  const um = UNIT_RE.exec(after);
  let factor = 1;
  let unit = '';
  if (um && !FLAG_WORDS.test(um[1].trim())) {
    const nu = normUnit(um[1]);
    if (test.units && Object.prototype.hasOwnProperty.call(test.units, nu)) {
      factor = test.units[nu];
      unit = um[1].trim();
    } else if (looksLikeUnit(nu)) {
      return { ok: false, reason: `Unit "${um[1].trim()}" is not supported for this field` };
    }
  }

  let value = raw;
  const notes = [];

  // "180 lb 4 oz" -> 180.25 lb
  if (test.id === 'weight' && factor === 1 && /^(?:lbs?|pounds?)$/i.test(unit)) {
    const ozMatch = /^\s*(?:lbs?|pounds?)\s*(\d+(?:\.\d+)?)\s*oz\b/i.exec(after);
    if (ozMatch) {
      value = String(roundTo(parseFloat(raw) + parseFloat(ozMatch[1]) / 16, 2));
      notes.push(`ounces converted (${raw} lb ${ozMatch[1]} oz)`);
    }
  }

  // Any further standalone number (other than reference ranges / times) makes the line ambiguous.
  const remainder = after
    .slice(um ? um[0].length : 0)
    .replace(/\d+(?:\.\d+)?\s*-\s*\d+(?:\.\d+)?/g, ' ')
    .replace(/[<>\u2264\u2265]=?\s*\d+(?:\.\d+)?/g, ' ')
    .replace(/\d{1,2}:\d{2}/g, ' ')
    .replace(/^\s*(?:lbs?|pounds?)\s*\d+(?:\.\d+)?\s*oz\b/i, ' ')
    .replace(/[A-Za-z\u00B5\u03BC][A-Za-z\u00B5\u03BC0-9.\u00B2/]*/g, ' ');
  if (/\d/.test(remainder)) {
    notes.push('more than one number on the line; only the first was used, please verify');
  }

  if (factor !== 1) {
    value = String(roundTo(parseFloat(raw) * factor, test.decimals ?? 2));
    notes.push(`converted from ${unit}`);
  } else if (!unit && test.scaleIfBelow && parseFloat(raw) < test.scaleIfBelow) {
    value = String(roundTo(parseFloat(raw) * 1000, 0));
    notes.push('no unit given; assumed K/uL and multiplied by 1000');
  }
  if (comparator) notes.push(`reported as "${comparator}${raw}"; the comparator is not stored`);
  return { ok: true, value, notes };
}

function splitLabChunks(text) {
  const parts = [];
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch !== ',' && ch !== ';' && ch !== '|') continue;
    const next = text.slice(i + 1).trim();
    if (!next) continue;
    const startsNewTest = !!matchTest(next) || !!startsWithUntracked(next);
    if (startsNewTest) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts.map((p) => p.trim()).filter(Boolean);
}

const SEG_SPLIT = /\)\s*[,;]\s*|\s*[;|]\s*/;

function parseVitalsLabs(entries, ctx) {
  const readingsByTest = new Map();
  const extra = { bp: [], weightLoss: [] };
  const addReading = (test, reading) => {
    if (!readingsByTest.has(test.id)) readingsByTest.set(test.id, []);
    readingsByTest.get(test.id).push(reading);
  };
  let order = 0;

  entries.forEach((e) => {
    let text = e.text.replace(/(\d),(\d{3})(?!\d)/g, '$1$2');
    if (isColumnHeaderLine(text) || /^(?:test|component|name)\b.*\b(?:result|value)\b/i.test(text)) return;

    // Leading date applies to every chunk on the line
    let leadingDate = null;
    const ld = findDate(text, { twoPart: true });
    if (ld && ld.index === 0) {
      leadingDate = ld;
      text = trimDelims(removeSpan(text, ld));
    }

    splitLabChunks(text).forEach((chunk) => {
      const tm = matchTest(chunk);
      if (!tm) {
        const untracked = startsWithUntracked(chunk);
        if (untracked) {
          ctx.skipped({ ...e, text: chunk }, 'Recognised, but not tracked in the Precharting chart');
        } else if (hasLetters(chunk)) {
          ctx.unrecognized({ ...e, text: chunk }, 'Not a recognised vitals/lab field');
        }
        return;
      }
      const { test } = tm;
      const rest = chunk.slice(tm.length).replace(/^[\s:=-]+/, '');

      if (test.special === 'bp') {
        const segs = rest.split(SEG_SPLIT).filter(Boolean);
        segs.forEach((seg) => {
          const dt = findDate(seg, { twoPart: false });
          const clean = dt ? removeSpan(seg, dt) : seg;
          const bpRe = /(\d{2,3})\s*\/\s*(\d{2,3})/g;
          let m = bpRe.exec(clean);
          if (!m) {
            ctx.unrecognized({ ...e, text: chunk }, 'Blood pressure value not found (expected e.g. 128/82)');
          }
          while (m) {
            extra.bp.push({ systolic: m[1], diastolic: m[2], date: dt || leadingDate, order: order++, entry: e });
            m = bpRe.exec(clean);
          }
        });
        return;
      }

      if (test.special === 'weightLoss') {
        const m = /(\d+(?:\.\d+)?)\s*(lbs?|pounds?|kg)\b[^0-9]*?(?:in|over|during|within|x)\s*(?:the\s+(?:last|past)\s+)?(\d+)\s*(day|week|month|year)s?/i.exec(rest);
        if (m) {
          const kg = /^kg/i.test(m[2]);
          extra.weightLoss.push({
            value: kg ? String(roundTo(parseFloat(m[1]) * 2.20462, 1)) : m[1],
            duration: m[3],
            durationUnit: `${m[4].toLowerCase()}s`,
            converted: kg,
            entry: e,
          });
        } else {
          ctx.unrecognized({ ...e, text: chunk }, 'Weight loss not understood (expected e.g. "Weight loss: 10 lbs in 3 months")');
        }
        return;
      }

      if (test.kind === 'text') {
        const textRest = rest.replace(/^\([^)]*\)\s*[:=-]?\s*/, '');
        const dt = findDate(textRest, { twoPart: false });
        const value = trimDelims(dt ? removeSpan(textRest, dt) : textRest);
        if (value) addReading(test, { value, date: dt || leadingDate, order: order++, entry: e, notes: [] });
        else ctx.unrecognized({ ...e, text: chunk }, 'No result found');
        return;
      }

      const segments = rest.split(SEG_SPLIT).filter((s) => s.trim());
      if (!segments.length) {
        ctx.unrecognized({ ...e, text: chunk }, 'No result found');
        return;
      }
      segments.forEach((seg) => {
        const dt = findDate(seg, { twoPart: true });
        const clean = dt ? removeSpan(seg, dt) : seg;
        const r = readNumeric(clean, test);
        if (!r.ok) {
          ctx.unrecognized({ ...e, text: chunk }, `${test.label}: ${r.reason}`);
          return;
        }
        addReading(test, { value: r.value, date: dt || leadingDate, order: order++, entry: e, notes: r.notes });
      });
    });
  });

  // Combine readings -> chart data
  const data = createEmptyVitalsLabs();
  const mapped = [];
  const warnings = [];
  const sortReadings = (readings, label) => {
    const allDated = readings.every((r) => r.date);
    if (allDated) {
      return [...readings].sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date) || a.order - b.order);
    }
    if (readings.length > 1) {
      warnings.push(`${label}: some readings have no date, so they were used in the order written (older first, newest last)`);
    }
    return [...readings].sort((a, b) => a.order - b.order);
  };
  const outOfRange = (test, value) => {
    if (!test.range) return;
    const n = parseFloat(value);
    if (!Number.isNaN(n) && (n < test.range[0] || n > test.range[1])) {
      warnings.push(`${test.label}: ${value} is outside the usual range ${test.range[0]}-${test.range[1]}; please verify`);
    }
  };

  TESTS.forEach((test) => {
    if (test.special) return;
    const readings = readingsByTest.get(test.id);
    if (!readings || !readings.length) return;
    const sorted = sortReadings(readings, test.label);
    const notes = [...new Set(sorted.flatMap((r) => r.notes || []))];
    const source = sorted.map((r) => r.entry.text).join(' / ');
    if (test.kind === 'pair') {
      const after = sorted.length > 1 ? sorted[sorted.length - 1] : null;
      const before = sorted.length > 1 ? sorted[sorted.length - 2] : sorted[0];
      setByPath(data, [...test.path, 'before'], before.value);
      if (after) setByPath(data, [...test.path, 'after'], after.value);
      if (sorted.length > 2) {
        notes.push(`${sorted.length} readings found; used the two most recent`);
      }
      if (sorted.length === 1) notes.push('single reading stored as "Before"');
      outOfRange(test, before.value);
      if (after) outOfRange(test, after.value);
      mapped.push({
        id: test.id, label: test.label, before: before.value, after: after ? after.value : '', notes, source,
      });
    } else {
      const chosen = sorted[sorted.length - 1];
      if (sorted.length > 1) notes.push(`${sorted.length} readings found; used the most recent`);
      setByPath(data, test.path, chosen.value);
      if (test.kind !== 'text') outOfRange(test, chosen.value);
      mapped.push({
        id: test.id, label: test.label, value: chosen.value, notes, source,
      });
    }
  });

  if (extra.bp.length) {
    const allDated = extra.bp.every((b) => b.date);
    const sorted = allDated
      ? [...extra.bp].sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date) || a.order - b.order)
      : [...extra.bp].sort((a, b) => a.order - b.order);
    if (!allDated && sorted.length > 1) {
      warnings.push('Blood pressure: some readings have no date, so the last one written was used');
    }
    const chosen = sorted[sorted.length - 1];
    data.bloodPressure = { systolic: chosen.systolic, diastolic: chosen.diastolic };
    const notes = sorted.length > 1 ? [`${sorted.length} readings found; used the most recent`] : [];
    const s = +chosen.systolic;
    const d = +chosen.diastolic;
    if (s < 70 || s > 250 || d < 40 || d > 150 || s <= d) {
      warnings.push(`Blood pressure ${chosen.systolic}/${chosen.diastolic} looks unusual; please verify`);
    }
    mapped.unshift({
      id: 'bp', label: 'Blood pressure (mmHg)', value: `${chosen.systolic}/${chosen.diastolic}`, notes, source: chosen.entry.text,
    });
  }

  if (extra.weightLoss.length) {
    const chosen = extra.weightLoss[extra.weightLoss.length - 1];
    data.weightLoss = { value: chosen.value, duration: chosen.duration, durationUnit: chosen.durationUnit };
    mapped.push({
      id: 'weightLoss', label: 'Weight loss',
      value: `${chosen.value} lbs in ${chosen.duration} ${chosen.durationUnit}`,
      notes: chosen.converted ? ['converted from kg'] : [], source: chosen.entry.text,
    });
  }

  return { data, mapped, warnings };
}

/* -------------------------------------------------------------------------- */
/* Main entry                                                                 */
/* -------------------------------------------------------------------------- */

const SECTION_ORDER = [
  'problemList', 'medicationList', 'vitalsLabs', 'surgicalHistory',
  'hospitalization', 'diagnosticImaging', 'procedures', 'specialistsVisits',
];

export const SECTION_LABELS = {
  problemList: 'ICD-10 Codes (Problem List)',
  medicationList: 'Medications',
  vitalsLabs: 'Vitals & Labs',
  surgicalHistory: 'Surgical History',
  hospitalization: 'Hospitalization',
  diagnosticImaging: 'Diagnostic Imaging',
  procedures: 'Procedures',
  specialistsVisits: 'Specialists',
};

export function parsePrechartingText(rawText) {
  const result = {
    sections: {}, unrecognized: [], skipped: [], ignoredSections: [], headingsFound: 0,
  };
  const text = String(rawText || '').replace(/\r\n?/g, '\n');
  const lines = text.split('\n');

  const buckets = {};
  const ignored = new Map();
  let current = { type: 'preamble' };

  lines.forEach((raw, idx) => {
    const lineNumber = idx + 1;
    const line = cleanLine(raw);
    if (!line || NOISE_LINE_RE.test(line)) return;

    const head = detectHeading(line, current.type === 'target' ? current : null);
    if (head) {
      if (head.type === 'target') {
        current = { type: 'target', key: head.key };
        if (!buckets[head.key]) buckets[head.key] = [];
        result.headingsFound += 1;
        if (head.remainder) buckets[head.key].push({ text: head.remainder, lineNumber });
      } else if (head.type === 'ignored') {
        current = { type: 'ignored', heading: head.heading };
        ignored.set(head.heading, (ignored.get(head.heading) || 0) + (line.includes(':') && line.split(':').slice(1).join(':').trim() ? 1 : 0));
      } else if (head.type === 'status') {
        buckets.medicationList.push({ kind: 'status', status: head.status, lineNumber, remainder: head.remainder });
      }
      return;
    }

    if (current.type === 'target') {
      buckets[current.key].push({ text: line, lineNumber });
    } else if (current.type === 'ignored') {
      ignored.set(current.heading, (ignored.get(current.heading) || 0) + 1);
    } else if (META_LINE_RE.test(line)) {
      ignored.set('Visit header', (ignored.get('Visit header') || 0) + 1);
    } else {
      result.unrecognized.push({
        section: null, lineNumber, text: line, reason: 'Appears before any recognised section heading',
      });
    }
  });

  ignored.forEach((count, heading) => result.ignoredSections.push({ heading, lineCount: count }));

  const makeCtx = (key) => {
    const warnings = [];
    return {
      warnings,
      warn: (entry, message) => warnings.push({ lineNumber: entry.lineNumber, text: entry.text, message }),
      unrecognized: (entry, reason) =>
        result.unrecognized.push({ section: key, lineNumber: entry.lineNumber, text: entry.text, reason }),
      skipped: (entry, reason) =>
        result.skipped.push({ section: key, lineNumber: entry.lineNumber, text: entry.text, reason }),
    };
  };

  SECTION_ORDER.forEach((key) => {
    const entries = buckets[key];
    if (!entries) return;
    const ctx = makeCtx(key);
    let section;
    switch (key) {
      case 'problemList':
        section = { items: parseProblems(entries, ctx) };
        break;
      case 'medicationList':
        section = { items: parseMedications(entries, ctx) };
        break;
      case 'vitalsLabs': {
        const v = parseVitalsLabs(entries, ctx);
        section = { data: v.data, mapped: v.mapped, items: v.mapped };
        v.warnings.forEach((message) => ctx.warnings.push({ message }));
        break;
      }
      case 'surgicalHistory':
        section = { items: parseSurgicalOrHospital(entries, 'surgery', ctx) };
        break;
      case 'hospitalization':
        section = { items: parseSurgicalOrHospital(entries, 'reason', ctx) };
        break;
      case 'diagnosticImaging':
        section = { items: parseImaging(entries, ctx) };
        break;
      case 'procedures':
        section = { items: parseProcedures(entries, ctx) };
        break;
      case 'specialistsVisits':
        section = { items: parseSpecialists(entries, ctx) };
        break;
      default:
        return;
    }
    section.warnings = ctx.warnings;
    result.sections[key] = section;
  });

  return result;
}

/* -------------------------------------------------------------------------- */
/* Applying to chart state                                                    */
/* -------------------------------------------------------------------------- */

const stripMeta = (item) => {
  const {
    sourceLine, lineNumber, parsedName, bareName, dbVerified, dbDescription, noteText, ...rest
  } = item;
  return rest;
};

/**
 * Builds the partial `prechartingData` update for the selected preview keys. Sections that were not
 * selected are not included, so they are left untouched (replace semantics apply only to selected ones).
 * `diagnosticImaging` only contains the sub-sections that were selected.
 */
export function buildChartUpdates(sections, selectedKeys) {
  const updates = {};
  selectedKeys.forEach((key) => {
    const s = sections[key];
    if (!s) return;
    switch (key) {
      case 'problemList':
        updates.problemList = s.items.map((p) => ({ id: p.id, code: p.code, description: p.description || '' }));
        break;
      case 'medicationList':
        updates.medicationList = { medications: s.items.map(stripMeta) };
        break;
      case 'vitalsLabs':
        updates.vitalsLabs = s.data;
        break;
      case 'surgicalHistory':
      case 'hospitalization':
      case 'diagnosticImaging':
        updates.diagnosticImaging = updates.diagnosticImaging || {};
        updates.diagnosticImaging[key] = s.items.map(stripMeta);
        break;
      case 'procedures':
      case 'specialistsVisits':
        updates[key] = s.items.map(stripMeta);
        break;
      default:
        break;
    }
  });
  return updates;
}

/* -------------------------------------------------------------------------- */
/* Single-section extraction (the per-tab "Extract from Text" buttons)         */
/* -------------------------------------------------------------------------- */

// Heading that is prepended to the pasted text so that it is read as the requested section.
const SECTION_HEADING = {
  problemList: 'Problem List',
  medicationList: 'Medications',
  vitalsLabs: 'Vitals and Labs',
  surgicalHistory: 'Surgical History',
  hospitalization: 'Hospitalization',
  diagnosticImaging: 'Diagnostic Imaging',
  procedures: 'Procedures',
  specialistsVisits: 'Specialists',
};

const shiftLine = (entry) => (
  entry && typeof entry.lineNumber === 'number' ? { ...entry, lineNumber: entry.lineNumber - 1 } : entry
);

/* ----- "Is this text for this tab?" detection ------------------------------ */

// How sure a signal is. strong/medium lines are treated as clearly belonging to another tab;
// soft ones (procedure / surgery / hospitalization wording overlaps a lot) only get a note.
const SECTION_LEVEL = {
  problemList: 'strong',
  medicationList: 'strong',
  vitalsLabs: 'strong',
  diagnosticImaging: 'medium',
  specialistsVisits: 'medium',
  procedures: 'soft',
  surgicalHistory: 'soft',
  hospitalization: 'soft',
};

const IMAGING_RE =
  /\b(?:x-?ray|radiograph|ct|cta|mri|mra|ultrasound|sonogram|echocardiogram|echo|mammogram|mammography|dexa|bone density|doppler|duplex|angiogram|fluoroscopy|ekg|ecg|holter|stress test|nuclear|pet\s*(?:scan|ct)|scan)\b/i;
const PROCEDURE_RE =
  /\b(?:colonoscopy|endoscopy|egd|biopsy|bronchoscopy|cystoscopy|colposcopy|sigmoidoscopy|ablation|catheterization|angioplasty|stent|dialysis|paracentesis|thoracentesis|lumbar puncture|nerve block|epidural|injection|cardioversion)\b/i;
const SURGICAL_RE =
  /\b\w+(?:ectomy|otomy|ostomy|plasty|pexy|orrhaphy)\b|\b(?:surgery|surgical|repair|replacement|bypass|fusion|resection|transplant|cesarean|c-section|arthroscopy|fixation|reconstruction|cabg|ligation|implant|amputation)\b/i;
const HOSPITAL_RE =
  /\b(?:hospitali[sz]ed|hospitali[sz]ation|admitted|admission|inpatient|icu|intensive care|er visit|emergency (?:room|department)|discharged)\b/i;
const SPECIALTY_RE =
  /^(?:cardiology|nephrology|endocrinology|neurology|gastroenterology|pulmonology|pulmonary|rheumatology|oncology|urology|dermatology|ophthalmology|orthopedics|orthopaedics|orthopedic|psychiatry|hematology|podiatry|ent|otolaryngology|gynecology|ob\/?gyn|infectious disease|pain management|allergy|immunology|neurosurgery|vascular surgery|general surgery|psychology|sleep medicine|geriatrics|hepatology)\b/i;
const DOCTOR_RE = /\bDr\.?\s+[A-Z][a-z]|\b[A-Z][A-Za-z'-]+,?\s+(?:MD|M\.D\.|DO|D\.O\.|NP|PA-C)\b/;

/** Tabs a single line of text could belong to, best guess first. Empty = nothing recognisable. */
function lineMatches(line) {
  let text = line;
  const leadingDate = findDate(text, { twoPart: true });
  if (leadingDate && leadingDate.index === 0) text = trimDelims(removeSpan(text, leadingDate));
  if (!text) return [];

  const strong = [];

  // ICD-10 code at the start of the line ("E11.9 -- Type 2 diabetes")
  const codes = extractIcdCodes(text);
  if (
    codes.length > 0 &&
    /^(?:icd[\s-]?10(?:[\s-]?cm)?\s*(?:code)?\s*[:#-]?\s*)?$/i.test(text.slice(0, codes[0].index))
  ) {
    strong.push('problemList');
  }

  // Medication: a strength together with a form / route / frequency ("... 500 mg tablet orally twice a day")
  const med = parseMedicationLine(text, 'taking', {});
  const medSignals = med && !med.bareName
    ? [med.dosage, med.formulation, med.frequency, med.route].filter(Boolean).length
    : 0;
  const looksLikeMed = medSignals >= 3 || (med && !med.bareName && !!med.dosage && !!med.formulation);
  if (looksLikeMed) strong.push('medicationList');

  // Vitals / labs: a known test name directly followed by a number ("HbA1c 7.5 %", "BP 128/82")
  if (!looksLikeMed) {
    const chunks = splitLabChunks(text.replace(/(\d),(\d{3})(?!\d)/g, '$1$2'));
    const hasReading = chunks.some((chunk) => {
      const tm = matchTest(chunk);
      const untracked = tm ? null : startsWithUntracked(chunk);
      const consumed = tm ? tm.length : (untracked ? untracked.length : -1);
      if (consumed < 0) return false;
      return /^[\s:=-]*(?:\([^)]*\)\s*)?[<>\u2264\u2265]?\s*\d/.test(chunk.slice(consumed));
    });
    if (hasReading) strong.push('vitalsLabs');
  }

  if (strong.length) return strong;

  const matches = [];
  if (IMAGING_RE.test(text)) matches.push('diagnosticImaging');
  if (SPECIALTY_RE.test(text) || DOCTOR_RE.test(text)) matches.push('specialistsVisits');
  if (PROCEDURE_RE.test(text)) matches.push('procedures');
  if (SURGICAL_RE.test(text)) matches.push('surgicalHistory');
  if (HOSPITAL_RE.test(text)) matches.push('hospitalization');
  return matches;
}

/**
 * Checks whether pasted text really belongs to `sectionKey` (the tab it was pasted in).
 *  - foreign:     lines that look like another tab: { lineNumber, text, suggested, level }
 *  - hardForeign: the clear ones (ICD codes, medications, vitals/labs, imaging, specialists)
 *  - mismatch:    most of the text is clearly for another tab, `suggestion` is the best tab for it
 * It never reads a line as "wrong" unless it positively recognises it as something else, so lines
 * without any recognisable wording (e.g. a bare hospitalization reason) are left alone.
 */
export function checkSectionFit(sectionKey, rawText) {
  const lines = String(rawText || '').replace(/\r\n?/g, '\n').split('\n');
  const foreign = [];
  let considered = 0;
  let own = 0;

  lines.forEach((raw, idx) => {
    const line = cleanLine(raw);
    if (!line || NOISE_LINE_RE.test(line) || !hasLetters(line)) return;
    if (detectHeading(line, null) || isColumnHeaderLine(line)) return;
    considered += 1;

    const matches = lineMatches(line);
    if (!matches.length) return;
    if (matches.includes(sectionKey)) {
      own += 1;
      return;
    }
    foreign.push({
      lineNumber: idx + 1,
      text: line,
      suggested: matches[0],
      level: SECTION_LEVEL[matches[0]],
    });
  });

  const hardForeign = foreign.filter((f) => f.level !== 'soft');
  const softForeign = foreign.filter((f) => f.level === 'soft');

  const votes = {};
  hardForeign.forEach((f) => { votes[f.suggested] = (votes[f.suggested] || 0) + 1; });
  const suggestion = Object.keys(votes).sort((a, b) => votes[b] - votes[a])[0] || null;

  const mismatch =
    hardForeign.length > 0 && hardForeign.length >= Math.ceil(considered / 2) && hardForeign.length > own;

  return { considered, own, foreign, hardForeign, softForeign, suggestion, mismatch };
}

/**
 * Parses text pasted into ONE tab. The text does not need a heading: it is always read as `sectionKey`.
 * A heading that belongs to a different section is still honoured (its lines are NOT used here) and is
 * reported in `otherSections`, so nothing is silently put in the wrong place.
 *
 * Returns { section: { items, warnings, data? }, unrecognized, skipped, otherSections, ignoredSections }
 * with line numbers relative to the pasted text.
 */
export function parseSectionText(sectionKey, rawText) {
  const heading = SECTION_HEADING[sectionKey];
  if (!heading) throw new Error(`Unknown precharting section: ${sectionKey}`);

  const full = parsePrechartingText(`${heading}\n${String(rawText || '')}`);
  const own = full.sections[sectionKey];
  const section = own
    ? { ...own, items: own.items.map(shiftLine), warnings: (own.warnings || []).map(shiftLine) }
    : { items: [], warnings: [] };

  return {
    section,
    unrecognized: full.unrecognized
      .filter((u) => u.section === sectionKey || u.section === null)
      .map(shiftLine),
    skipped: full.skipped.filter((s) => s.section === sectionKey).map(shiftLine),
    otherSections: Object.keys(full.sections).filter((k) => k !== sectionKey),
    ignoredSections: full.ignoredSections,
  };
}

/** Items of a parsed section in the exact shape stored by the matching Precharting form. */
export function getSectionItems(sectionKey, section) {
  if (!section || !Array.isArray(section.items)) return [];
  if (sectionKey === 'problemList') {
    return section.items.map((p) => ({ id: p.id, code: p.code, description: p.description || '' }));
  }
  return section.items.map(stripMeta);
}

/**
 * Reads text that has NO recognised section heading (e.g. a few lines pasted straight into a tab).
 * It is read as `defaultSectionKey` (the tab it was pasted in) - unless most of the lines clearly belong
 * to another section, in which case it is stored there instead. Lines that clearly belong to yet another
 * section are left out and listed under "Not added", so nothing is silently put in the wrong place.
 *
 * Returns the same shape as parsePrechartingText(), plus:
 *  - targetKey:    the section the text was stored in
 *  - redirectedTo: set when the text was moved away from `defaultSectionKey`
 */
export function parseHeadinglessText(defaultSectionKey, rawText) {
  const fit = checkSectionFit(defaultSectionKey, rawText);
  const redirected = fit.mismatch && fit.suggestion && fit.suggestion !== defaultSectionKey;
  const targetKey = redirected ? fit.suggestion : defaultSectionKey;

  const parsed = parseSectionText(targetKey, rawText);
  let { section, unrecognized } = parsed;

  const foreign = redirected ? [] : fit.hardForeign;
  if (foreign.length > 0) {
    const foreignLines = new Set(foreign.map((f) => f.lineNumber));
    section = { ...section, items: section.items.filter((i) => !foreignLines.has(i.lineNumber)) };
    unrecognized = unrecognized.filter((u) => !foreignLines.has(u.lineNumber));
    foreign.forEach((f) => {
      unrecognized.push({
        section: targetKey,
        lineNumber: f.lineNumber,
        text: f.text,
        reason: `Looks like ${SECTION_LABELS[f.suggested] || f.suggested}. Paste it with a "${SECTION_HEADING[f.suggested] || SECTION_LABELS[f.suggested]}" heading to add it`,
      });
    });
  }

  return {
    sections: { [targetKey]: section },
    unrecognized,
    skipped: parsed.skipped.map((s) => ({ ...s, section: targetKey })),
    ignoredSections: parsed.ignoredSections,
    headingsFound: 0,
    targetKey,
    redirectedTo: redirected ? targetKey : null,
  };
}

const isBlank = (v) => v === undefined || v === null || v === '';
const isBeforeAfter = (o) =>
  !!o && typeof o === 'object' && !Array.isArray(o) && Object.keys(o).length === 2 && 'before' in o && 'after' in o;

/**
 * Merges freshly extracted vitals/labs into the existing form data. Only values that were actually
 * found in the text are written; everything else (including values typed earlier) is kept.
 * A Before/After pair is replaced as a unit so it never mixes an old and a new reading.
 */
export function mergeVitalsLabs(existing, parsed) {
  const incoming = { ...(parsed || {}) };
  // "durationUnit" has a default, so weight loss only counts when a value was actually extracted
  if (!incoming.weightLoss || isBlank(incoming.weightLoss.value)) delete incoming.weightLoss;

  const merge = (current, next) => {
    if (isBeforeAfter(next)) {
      return !isBlank(next.before) || !isBlank(next.after) ? { ...next } : current;
    }
    if (next && typeof next === 'object' && !Array.isArray(next)) {
      const out = { ...(current && typeof current === 'object' ? current : {}) };
      Object.keys(next).forEach((k) => {
        const merged = merge(out[k], next[k]);
        if (merged !== undefined) out[k] = merged;
      });
      return out;
    }
    return isBlank(next) ? current : next;
  };

  return merge(existing || {}, incoming);
}

const norm = (v) => String(v ?? '').trim().toLowerCase();

const appendUnique = (existing, incoming, signature) => {
  const list = Array.isArray(existing) ? existing : [];
  const seen = new Set(list.map(signature));
  const fresh = [];
  (incoming || []).forEach((item) => {
    const sig = signature(item);
    if (seen.has(sig)) return;
    seen.add(sig);
    fresh.push(item);
  });
  return { list: [...list, ...fresh], added: fresh.length, skipped: (incoming || []).length - fresh.length };
};

const ITEM_SIGNATURE = {
  problemList: (p) => norm(p.code),
  medicationList: (m) => [m.name || m.medicationName, m.dosage, m.frequency].map(norm).join('|'),
  surgicalHistory: (s) => [s.surgery, s.date].map(norm).join('|'),
  hospitalization: (h) => [h.reason, h.date].map(norm).join('|'),
  diagnosticImaging: (d) => [d.date, d.locationContrast, d.indication, d.result].map(norm).join('|'),
  procedures: (p) => [p.name, p.date, p.indication, p.result].map(norm).join('|'),
  specialistsVisits: (v) => [v.date, v.specialty, v.specialistName, v.details].map(norm).join('|'),
};

/**
 * "Add to existing" version of applying buildChartUpdates() output: new entries are appended to what is
 * already in each section (exact duplicates are skipped) and vitals/labs only fill the values found.
 * Returns { data, summary } - `data` is a partial prechartingData (spread it over the current one) and
 * `summary[sectionKey] = { added, skipped }`.
 */
export function mergeChartUpdates(current, updates) {
  const data = {};
  const summary = {};
  const cur = current || {};

  if (updates.problemList) {
    const r = appendUnique(cur.problemList, updates.problemList, ITEM_SIGNATURE.problemList);
    data.problemList = r.list;
    summary.problemList = { added: r.added, skipped: r.skipped };
  }
  if (updates.medicationList) {
    const r = appendUnique(
      cur.medicationList?.medications,
      updates.medicationList.medications,
      ITEM_SIGNATURE.medicationList,
    );
    data.medicationList = { ...(cur.medicationList || {}), medications: r.list };
    summary.medicationList = { added: r.added, skipped: r.skipped };
  }
  if (updates.vitalsLabs) {
    data.vitalsLabs = mergeVitalsLabs(cur.vitalsLabs, updates.vitalsLabs);
    summary.vitalsLabs = { added: 1, skipped: 0 };
  }
  if (updates.diagnosticImaging) {
    const base = {
      surgicalHistory: [],
      hospitalization: [],
      diagnosticImaging: [],
      ...(cur.diagnosticImaging || {}),
    };
    Object.keys(updates.diagnosticImaging).forEach((key) => {
      const r = appendUnique(base[key], updates.diagnosticImaging[key], ITEM_SIGNATURE[key]);
      base[key] = r.list;
      summary[key] = { added: r.added, skipped: r.skipped };
    });
    data.diagnosticImaging = base;
  }
  ['procedures', 'specialistsVisits'].forEach((key) => {
    if (!updates[key]) return;
    const r = appendUnique(cur[key], updates[key], ITEM_SIGNATURE[key]);
    data[key] = r.list;
    summary[key] = { added: r.added, skipped: r.skipped };
  });

  return { data, summary };
}

export const PASTE_TEMPLATE = `DIAGNOSIS LIST
1) E11.9 -- Type 2 diabetes mellitus without complications
2) I10 -- Essential (primary) hypertension - I10 (Primary)/I12.9 - Hypertensive Chronic Kidney Disease Stage 1 to 4
3) N18.3 -- Chronic Kidney Disease Stage 3

CURRENT MEDICATIONS
Taking
Metformin 500 mg tablet 1 tab(s) orally 2 times a day
Lisinopril 10 mg tablet 1 tab(s) orally once a day
Lantus SoloStar 100 unit/mL solution 20 units subcutaneously once a day (at bedtime)
Not-Taking
Atorvastatin 40 mg tablet 1 tab(s) orally QHS
Discontinued
Glipizide 5 mg tablet 1 tab(s) orally once a day

SURGICAL HISTORY
Right distal femur fracture repair 10/15

HOSPITALIZATION/MAJOR DIAGNOSTIC PROCEDURE
Influenza with dehydration 01/24

VITAL SIGNS
Temp 98.2 F, Pulse Ox 97, HR 76, BP 128/82 mm of Hg, Wt 180 lbs, Ht 70, BMI 28.50 Index

LABS/DI/REPORTS
HbA1c 7.5 % 06/10/2024
HbA1c 6.8 % 01/15/2025
LDL Cholesterol 142 mg/dL 06/10/2024
LDL Cholesterol 108 mg/dL 01/15/2025
Creatinine 1.1 mg/dL
Sodium 139 mmol/L
Potassium 4.4

DIAGNOSTIC IMAGING
03/10/24, CT chest with contrast, Cough, No acute abnormality

PROCEDURES
01/15/24, Colonoscopy, Routine screening, Normal - no polyps

SPECIALISTS
03/10/24, Cardiology, Dr. Smith, Follow-up for hypertension - BP well controlled`;
